import * as cornerstone from "@cornerstonejs/core";
import $ from "jquery";
import { ViewportType } from "@cornerstonejs/core/enums";
import * as cornerstonetools from "@cornerstonejs/tools";
import {
  getRenderingEngineLocal,
  getToolGroupManager,
  getVOISynchronizer,
  makeBytesAvailableInCache,
  evictSeriesFromCache,
} from "./CornerstoneService";
import {
  bindViewportStackPrefetch,
  getThumbnailViewport,
} from "./CornerstoneThumbnailService";
import hardcodedMetaDataProvider from "./MetadataProvider";
import { checkEngineContextLost } from "./CornerstoneWebGLRecovery";
import SeriesStackScrollMouseWheelTool from "./SeriesStackScrollMouseWheelTool";
import { MouseBindings } from "@cornerstonejs/tools/enums";
import {
  HP_VIEWPORT_ALIGNMENT,
  VIEWPORT_COLUMN,
  VIEWPORT_ROW,
  MAX_MPR_VOLUME_BYTES,
} from "../../../constants/Constants";
import { PASSIVE_TOOLs } from "../../../constants/Constants";
import {
  DEFAULT_MOUSE_BUTTON_TOOL_ASSIGNMENTS,
  MOUSE_ASSIGNABLE_TOOL_NAMES,
} from "../../../constants/MouseButtonToolAssignments";
import { utilities } from "@cornerstonejs/tools";
import { getAllThumbnailViewports } from "./CornerstoneThumbnailService";
import { loadAndCacheImage } from "@cornerstonejs/core/loaders/imageLoader";
import { eventTarget } from "@cornerstonejs/core";
import { Enums, annotation } from "@cornerstonejs/tools";
import { _base64ToArrayBuffer } from "../../../service/StudyViewerService";
import {
  getImageSliceDataForVolumeViewport,
} from "@cornerstonejs/core/utilities";
import vtkImageData from "@kitware/vtk.js/Common/DataModel/ImageData";
import vtkDataArray from "@kitware/vtk.js/Common/Core/DataArray";
import isRgbaSourceRgbDestJsx from "./isRgbaSourceRgbDest.jsx";
import { MPRViewportsId, getMPRViewportInputs, VOLUME_3D_VIEWPORT_ID, VOLUME_3D_VIEWPORT_INDEX, get3DViewportInput } from "./MPRViewportInput";
import {
  buildFrameImageIds,
  buildWadorsFrameImageIds,
  detectMultiFrame,
  enqueueFramePrefetch,
  extractInstanceUids,
  fetchAndRegisterMultiFrameMetadata,
  getImageMetadata,
  isFrameCached,
  registerImageMetadata,
  resolveInitialFps,
  setUserPreferredCineFps,
  advancePrefetchWindow,
  PREFETCH_LOOK_AHEAD,
  PREFETCH_LOOK_BEHIND,
} from "./MultiFrameService";
import {
  MultiFramePlaybackEngine,
  registerEngine,
  disposeEngine,
  getEngine,
} from "./MultiFramePlaybackEngine";
import { getMultiFrameAutoStart } from "./multiFrameSettings";
import {
  subscribeBroadcast,
  SYNC_MODES,
} from "./MultiFramePlaybackBus";
import {
  createAndCacheVolume,
} from "@cornerstonejs/core/loaders/volumeLoader";
import { vec3 } from "gl-matrix";
import {
  orientation,
} from "@cornerstonejs/tools/utilities";
import { isMobile } from "react-device-detect";
import { getEnv } from '../../../helpers/EnvHelper';

// Re-export so viewer components (which import from this module) can push the
// user's cine FPS preference without reaching into MultiFrameService directly.
export { setUserPreferredCineFps };

// Bus subscriptions keyed by viewport id so we can tear them down with the
// engine. Lives at module scope because loadActiveViewport is non-React.
const busSubscriptions = new Map();

const disposeBusSubscription = (viewportId) => {
  const unsub = busSubscriptions.get(viewportId);
  if (unsub) {
    unsub();
    busSubscriptions.delete(viewportId);
  }
};

// Cache-sync pollers, keyed by viewport id. Each periodically recomputes which
// of an instance's frames are ACTUALLY in the cornerstone cache and pushes that
// to the engine (engine.syncLoadedFrames), so the toolbar's buffer bar mirrors
// the real cache — filling as frames prefetch and receding as they evict —
// rather than a monotonic "ever played/loaded" count.
const cacheSyncTimers = new Map();

const stopCacheSync = (viewportId) => {
  const id = cacheSyncTimers.get(viewportId);
  if (id != null) {
    clearInterval(id);
    cacheSyncTimers.delete(viewportId);
  }
};

const CACHE_SYNC_INTERVAL_MS = 250;

const startCacheSync = (viewportId, engine, frameImageIds) => {
  stopCacheSync(viewportId);
  if (!engine || !Array.isArray(frameImageIds) || frameImageIds.length === 0) {
    return;
  }
  const tick = () => {
    // Self-clean when this engine is gone or has been replaced (study change,
    // scroll to another instance, viewport teardown) — robust regardless of
    // which code path disposed it.
    if (getEngine(viewportId) !== engine) {
      stopCacheSync(viewportId);
      return;
    }
    const loaded = new Set();
    for (let i = 0; i < frameImageIds.length; i++) {
      if (isFrameCached(frameImageIds[i])) loaded.add(i);
    }
    engine.syncLoadedFrames(loaded);
  };
  tick(); // immediate first sync so the bar reflects the cache without a delay
  cacheSyncTimers.set(viewportId, setInterval(tick, CACHE_SYNC_INTERVAL_MS));
};

// Wheel handlers for the single-multi-frame case (NOT mixed-series —
// those live in viewportMixedSeriesState). Keyed by viewport id so we
// can detach when loadActiveViewport replaces the engine.
const singleFrameWheelHandlers = new Map();

const disposeSingleFrameWheelHandler = (viewportId, element) => {
  const handler = singleFrameWheelHandlers.get(viewportId);
  if (!handler) return;
  if (element) {
    try {
      element.removeEventListener("wheel", handler, { capture: true });
    } catch (err) {
      // element may already be torn down — swallow
    }
  }
  singleFrameWheelHandlers.delete(viewportId);
};

// --- Mixed-series two-axis cine orchestration ----------------------------
//
// For multi-image series that contain one or more multi-frame instances
// (e.g. 5 instances, 2 of which are 100-frame cines), the viewport runs in
// two modes:
//
//   OUTER mode  — viewport.stack = [imageId per instance]
//                 Scroll wheel moves between instances.
//                 Multi-frame slots render frame 1.
//                 This is the default; the thumbnail panel uses the same
//                 stack.
//
//   INNER mode  — viewport.stack = [frame1, frame2, ..., frameN] for the
//                 currently-active multi-frame instance.
//                 Scroll wheel scrubs frames within this instance.
//                 Cine plays through these N frames, looping within
//                 the instance.
//
// State per viewport: { outerStack, instanceFrameStacks, currentOuterIdx,
// cineMode, stackNewImageHandler }. Cleared in disposeMixedSeriesState
// when the viewport is reloaded.
const viewportMixedSeriesState = new Map();

const disposeMixedSeriesState = (viewportId) => {
  const state = viewportMixedSeriesState.get(viewportId);
  if (!state) return;
  if (state.stackNewImageHandler && state.viewportElement) {
    try {
      state.viewportElement.removeEventListener(
        cornerstone.EVENTS.STACK_NEW_IMAGE,
        state.stackNewImageHandler
      );
    } catch (err) {
      // element may already be torn down — swallow
    }
  }
  if (state.wheelHandler && state.viewportElement) {
    try {
      state.viewportElement.removeEventListener("wheel", state.wheelHandler, {
        capture: true,
      });
    } catch (err) {
      // element may already be torn down — swallow
    }
  }
  if (state.engineSetupTimer != null) {
    clearTimeout(state.engineSetupTimer);
    state.engineSetupTimer = null;
  }
  viewportMixedSeriesState.delete(viewportId);
};

// beforePlay hook for the engine — swaps viewport.stack from OUTER to
// INNER (current instance's frame array) and toggles cineMode. Returns
// false if the active instance is single-frame (engine should not start).
//
// IMPORTANT: cineMode must flip to "INNER" BEFORE setStack runs.
// setStack(frames, 0) synchronously fires STACK_NEW_IMAGE with
// imageIdIndex=0, and our handler interprets that as the user scrolling
// to outer index 0 (the top of the series) if cineMode is still "OUTER".
// Setting cineMode first makes the handler ignore the swap event.
const orchestrateMixedSeriesPlay = async (viewportId) => {
  const state = viewportMixedSeriesState.get(viewportId);
  if (!state) return true; // single-instance multi-frame — no orchestration
  if (state.cineMode === "INNER") return true; // already mid-cine
  const frames = state.instanceFrameStacks.get(state.currentOuterIdx);
  if (!frames || frames.length < 2) return false; // can't cine a single-frame slot
  state.cineMode = "INNER"; // see comment above — must flip before setStack
  try {
    await state.viewport.setStack(frames, 0);
    state.viewport.render();
    return true;
  } catch (err) {
    console.debug("[MultiFrame] failed to enter INNER mode:", err);
    state.cineMode = "OUTER"; // roll back so scroll keeps working
    return false;
  }
};

// Pausing a mixed-series cine deliberately does NOT restore the OUTER stack:
// the viewport stays INNER on the paused frame (Advance Viewer parity — a
// paused clip shows the frame you paused on, so frame-scoped PR annotations
// stay visible). Navigation back to the instance axis is handled by the wheel
// handler, updateViewport (scrollbar / nav widget) and stepMixedSeriesInstance
// (arrow keys), which all swap to OUTER themselves.

// Ensures the mixed-series viewport shows `frame` (0-based) of the current
// instance, entering INNER mode first when it is still on the OUTER stack.
// Used by the engine's onFrame so a paused step / scrub moves through FRAMES
// of the instance — a plain setImageIdIndex on the OUTER stack would jump to
// another INSTANCE instead.
const showMixedSeriesFrame = (viewport, frame) => {
  const state = viewportMixedSeriesState.get(viewport.id);
  const frames = state?.instanceFrameStacks.get(state.currentOuterIdx);
  if (!state || !frames) {
    viewport.setImageIdIndex(frame);
    viewport.render();
    return;
  }
  if (state.cineMode === "INNER") {
    viewport.setImageIdIndex(frame);
    viewport.render();
    return;
  }
  // Flip BEFORE setStack — see orchestrateMixedSeriesPlay.
  state.cineMode = "INNER";
  Promise.resolve(viewport.setStack(frames, frame))
    .then(() => viewport.render())
    .catch((err) => {
      console.debug("[MultiFrame] failed to enter INNER mode for frame:", err);
      state.cineMode = "OUTER";
    });
};

// Swap a mixed-series viewport from INNER (frames) back to OUTER (instances)
// at `outerIdx`, re-binding the engine to that instance. Returns false when
// the viewport has no mixed-series state.
const showMixedSeriesInstance = async (viewport, outerIdx) => {
  const state = viewportMixedSeriesState.get(viewport.id);
  if (!state) return false;
  const engine = getEngine(viewport.id);
  if (engine && engine.isPlaying) engine.pause({ skipAfterPause: true });
  const clamped = Math.max(0, Math.min(state.outerStack.length - 1, outerIdx));
  state.cineMode = "OUTER";
  state.currentOuterIdx = clamped;
  await viewport.setStack(state.outerStack, clamped);
  viewport.render();
  // Re-bind the engine (and toolbar) to the active instance — it would
  // otherwise stay pinned to the previous instance's frame count / cursor.
  // No autoplay: navigating shouldn't start a cine.
  setupMixedSeriesEngineForInstance(viewport, false);
  return true;
};

/**
 * Arrow-key instance navigation. In a mixed series the live stack may be the
 * INNER frame array (playing or paused on a frame), so stepping the raw
 * imageIdIndex would move FRAMES; this steps INSTANCES instead. Returns false
 * when the viewport has no mixed-series state (caller does its own stepping).
 */
export const stepMixedSeriesInstance = async (viewportIndex, delta) => {
  const viewport = await getViewport(viewportIndex);
  const state = viewport && viewportMixedSeriesState.get(viewport.id);
  if (!state) return false;
  if (state.cineMode !== "INNER") {
    // OUTER: the live stack IS the instance stack — step it directly.
    const idx = viewport.getCurrentImageIdIndex();
    const next = Math.max(0, Math.min(state.outerStack.length - 1, idx + delta));
    if (next !== idx) {
      await viewport.setImageIdIndex(next);
      viewport.render();
    }
    return true;
  }
  return showMixedSeriesInstance(viewport, state.currentOuterIdx + delta);
};

/**
 * Bring a viewport to a specific FRAME (0-based) of the instance `sopUid`,
 * paused — used to land on the frame a multi-frame PR is scoped to.
 *  • mixed series   → switch to that instance, enter INNER, seek to the frame
 *  • single-instance multi-frame → seek the engine
 *  • plain stack    → scroll to the slot carrying that SOP
 * `matchesSop(imageId)` decides whether an imageId belongs to `sopUid`.
 * Returns true when the viewport now shows the requested frame.
 */
export const focusInstanceFrame = async (viewportIndex, matchesSop, frame) => {
  const viewport = await getViewport(viewportIndex);
  if (!viewport) return false;
  const engine = getEngine(viewport.id);
  const state = viewportMixedSeriesState.get(viewport.id);

  if (state) {
    const outerIdx = state.outerStack.findIndex((id) => matchesSop(id));
    if (outerIdx === -1) return false;
    const frames = state.instanceFrameStacks.get(outerIdx);
    if (engine && engine.isPlaying) engine.pause({ skipAfterPause: true });
    if (!frames) {
      // Single-frame slot in a mixed series.
      if (outerIdx !== state.currentOuterIdx || state.cineMode === "INNER") {
        await showMixedSeriesInstance(viewport, outerIdx);
      }
      return true;
    }
    const target = Math.max(0, Math.min(frames.length - 1, frame));
    // A scroll into this instance debounces its engine rebind (300 ms); flush
    // it now, or we would seek the PREVIOUS instance's engine and the delayed
    // rebind would then reset the cursor to frame 1.
    const rebindPending = state.engineSetupTimer != null;
    if (rebindPending) {
      clearTimeout(state.engineSetupTimer);
      state.engineSetupTimer = null;
    }
    if (outerIdx !== state.currentOuterIdx) {
      // Different instance: bind the engine to it first (OUTER), then the
      // engine seek enters INNER at the frame via showMixedSeriesFrame.
      state.cineMode = "OUTER";
      state.currentOuterIdx = outerIdx;
      await viewport.setStack(state.outerStack, outerIdx);
      setupMixedSeriesEngineForInstance(viewport, false);
    } else if (rebindPending) {
      setupMixedSeriesEngineForInstance(viewport, false);
    }
    const eng = getEngine(viewport.id);
    if (eng) eng.seekTo(target);
    else showMixedSeriesFrame(viewport, target);
    return true;
  }

  const ids = viewport.getImageIds?.() || [];
  if (engine) {
    // Single-instance multi-frame: the stack is the instance's frames.
    if (!ids.length || !matchesSop(ids[0])) return false;
    if (engine.isPlaying) engine.pause();
    engine.seekTo(frame);
    return true;
  }
  const idx = ids.findIndex((id) => matchesSop(id));
  if (idx === -1) return false;
  if (viewport.getCurrentImageIdIndex() !== idx) {
    await viewport.setImageIdIndex(idx);
    viewport.render();
  }
  return true;
};

/**
 * Every frame imageId a mixed-series viewport can show, grouped per instance:
 * [{ outerImageId, frames: [frame1Id, frame2Id, …] | null }]. Null when the
 * viewport has no mixed-series state. Lets the PR service target a frame that
 * is not in the LIVE stack (OUTER mode only holds one slot per instance).
 */
export const getMixedSeriesInstances = (viewportId) => {
  const state = viewportMixedSeriesState.get(viewportId);
  if (!state) return null;
  return state.outerStack.map((outerImageId, i) => ({
    outerImageId,
    frames: state.instanceFrameStacks.get(i) ?? null,
  }));
};

/**
 * For viewports that have mixed-series state registered, returns the
 * "outer" (per-instance) index — i.e. the position the vertical scrollbar
 * should display. During INNER mode (cine playing through frames of one
 * instance) the viewport's actual imageIdIndex reflects the frame, but
 * the scrollbar should stay pinned to the instance the user navigated to.
 *
 * Returns null for viewports without mixed-series state; callers fall
 * back to `viewport.getCurrentImageIdIndex()` in that case.
 */
export const getCurrentOuterIndex = (viewportId) => {
  const state = viewportMixedSeriesState.get(viewportId);
  if (!state) return null;
  return state.currentOuterIdx;
};

export const getOuterStackLength = (viewportId) => {
  const state = viewportMixedSeriesState.get(viewportId);
  if (!state) return null;
  return state.outerStack.length;
};

// Walks a stack's imageIds and awaits the /metadata fetch for every
// wadors:.../frames/N slot. The wadors loader needs rows/cols/bits
// before it can decode a frame; the helper is idempotent per SOP UID
// and returns the cached promise when the registration already
// completed, so this is cheap on the warm path.
const ensureWadorsMetadataReady = async (imageIds) => {
  if (!Array.isArray(imageIds) || imageIds.length === 0) return;
  const seenSops = new Set();
  const pending = [];
  for (const imageId of imageIds) {
    if (typeof imageId !== "string" || !imageId.startsWith("wadors:")) continue;
    const meta = getImageMetadata(imageId);
    if (!meta || !meta.studyUid || !meta.seriesUid || !meta.sopUid) continue;
    if (seenSops.has(meta.sopUid)) continue;
    seenSops.add(meta.sopUid);
    pending.push(
      fetchAndRegisterMultiFrameMetadata({
        studyUid: meta.studyUid,
        seriesUid: meta.seriesUid,
        sopUid: meta.sopUid,
        numberOfFrames: meta.numberOfFrames,
        // Pass the wadors frame URL so shared paths (/sharedServices/dicomweb/)
        // derive their metadata URL from it instead of using the JWT endpoint.
        frame1ImageId: imageId,
      })
    );
  }
  if (pending.length) {
    await Promise.all(pending);
  }
};

// Cache-warmup threshold used to gate the first cine play. Without it the
// engine starts ticking before enough frames are cached and the first loop
// stutters; once N frames are in cache, frame-to-frame switches are cheap
// and the engine sustains its target FPS.
//
// Kept small (matches the throttle queue's concurrency) so warmup resolves
// after the FIRST batch of prefetches lands instead of waiting on a deep
// queue to drain. Frames beyond this are still enqueued in the background
// — they just don't block play().
const PREFETCH_WARMUP_FRAMES = 12;

// Hard ceiling on how long the engine waits for warmup before starting to
// tick anyway. Without this, a slow backend or a stalled XHR could leave
// the play button looking broken — engine isPlaying=true but no frames
// advance because warmup never resolves.
//
// Sized for a cold backend cache: the FIRST request for an un-split
// multi-frame instance triggers cloud download of the source DICOM and
// the offset-table build before any frame bytes come back, which can
// take 5–10s. A 2s timeout was firing before the first frame even
// landed, so the engine started ticking against an empty cache and
// the first cine loop appeared frozen until the user paused and
// re-played. 15s gives the backend extraction step time to finish.
const PREFETCH_WARMUP_TIMEOUT_MS = 15000;

// Mobile-only size of the HIGH-priority forward window in the initial warmup.
// Frames within this window of the current frame are fetched at high priority
// (playback-critical); frames beyond it are fetched at LOW priority so they
// trickle in behind, filling the buffer bar gradually (YouTube-style) without
// competing with playback. Peak memory is bounded by the cornerstone cache cap
// (which evicts once full), not by limiting what we enqueue. Sized comfortably
// above PREFETCH_LOOK_AHEAD (no gap when play starts) and above
// PREFETCH_WARMUP_FRAMES (the warmup promise still resolves from this window).
const MOBILE_CINE_PREFETCH_WINDOW = 40;

// Mobile-only HARD ceiling on how many frames ahead the initial warmup is
// allowed to prefetch at all. Relying on the cornerstone cache cap to bound
// memory is NOT enough for long cines: eagerly enqueueing a 700+-frame
// instance spikes in-flight decode buffers + cache + the CPU-render copies
// past the iOS per-tab RAM limit and the tab reloads. We never warm more than
// this many frames ahead; the rest are demand-loaded by the sliding window as
// the cursor reaches them (frames behind evict). This bounds the buffer bar's
// fill on very long cines — the played region still fills the whole track as
// you watch. Kept well below the frame count that was observed to OOM.
const MOBILE_MAX_PREFETCH_FRAMES = 120;

// Fire-and-forget cache warmup for multi-frame playback. Returns a promise
// that resolves once at least PREFETCH_WARMUP_FRAMES (or all frames if
// fewer) have finished loading — successfully or not. The engine awaits
// this on its first play() so the first cine loop is smooth.
//
// `onFrameLoaded(frameIndex)` is invoked as each frame settles (success
// OR failure — failures still count so a stuck loader doesn't get
// pinned on forever). The engine uses this to drive its
// `loadedFrameCount` / `isFullyLoaded` snapshot fields, which the
// toolbar reads for its buffering indicator.
//
// Skips the frame that's already on screen (Cornerstone has just loaded it
// via setStack). Failures are counted toward the threshold so a few bad
// frames don't hang autoplay.
// Desktop wadouri warmup concurrency. The wadouri path serves every ?frame=N
// slot from ONE already-downloaded DICOM, so real decode parallelism is bound
// by the web-worker pool (~7) no matter how many loads we enqueue. A small
// rolling window is enough — and, unlike fanning all N frames into the 50-slot
// prefetch semaphore at once, it can't stall the tail (each worker awaits its
// own load, so one slow frame blocks only itself, not the whole queue).
const WADOURI_WARMUP_CONCURRENCY = 8;

// Bounded rolling-window warmup for the wadouri whole-file multi-frame path
// (priors, legacy). Resolves the returned (play-gate) promise once
// PREFETCH_WARMUP_FRAMES have settled, then keeps loading the rest in the
// background so the buffer bar reaches 100% at idle without a click-to-play.
// Loads go straight through loadAndCacheImage — cornerstone dedupes in-flight /
// cached ids, and bypassing the shared prefetch semaphore means a stall in one
// frame can't wedge the others (the observed "stuck before the tail" bug).
const prefetchWadouriFramesBounded = (imageIds, currentIndex, onFrameLoaded) => {
  const notify = (idx) => {
    if (typeof onFrameLoaded === "function") onFrameLoaded(idx);
  };
  // Frame at currentIndex is already loaded by setStack.
  if (typeof currentIndex === "number") {
    Promise.resolve().then(() => notify(currentIndex));
  }

  const threshold = Math.min(PREFETCH_WARMUP_FRAMES, imageIds.length);
  let settled = 1; // current frame counts toward the play gate
  let gateResolved = false;
  let resolveGate;
  const gate = new Promise((r) => {
    resolveGate = r;
  });
  const tryResolveGate = () => {
    if (!gateResolved && settled >= threshold) {
      gateResolved = true;
      resolveGate();
    }
  };
  // Hard fallback so the play gate can never hang on a slow backend.
  setTimeout(() => {
    if (!gateResolved) {
      gateResolved = true;
      resolveGate();
    }
  }, PREFETCH_WARMUP_TIMEOUT_MS);

  // Work list: every frame except the current one; already-cached frames are
  // counted immediately so revisits show 100% at once.
  const queue = [];
  for (let i = 0; i < imageIds.length; i++) {
    if (i === currentIndex) continue;
    if (isFrameCached(imageIds[i])) {
      notify(i);
      settled += 1;
      continue;
    }
    queue.push(i);
  }
  tryResolveGate();

  let next = 0;
  const worker = async () => {
    while (next < queue.length) {
      const i = queue[next++];
      try {
        await loadAndCacheImage(imageIds[i], {
          requestType: "prefetch",
          priority: 5,
        });
      } catch (_) {
        // Rare on a single shared file; demand-load on play covers anything
        // that slips through. Count it as settled so the gate / background
        // pass can never hang waiting on it.
      }
      notify(i);
      settled += 1;
      tryResolveGate();
    }
  };
  const workerCount = Math.min(WADOURI_WARMUP_CONCURRENCY, queue.length);
  for (let k = 0; k < workerCount; k++) worker();

  return gate;
};

const prefetchFrameImageIds = (
  imageIds,
  currentIndex,
  onFrameLoaded,
  maxInitialFrames = Infinity
) => {
  if (!Array.isArray(imageIds) || imageIds.length < 2) {
    // Single-frame case — nothing to prefetch, but notify the current
    // frame so the engine's loaded-frame counter is accurate.
    if (typeof onFrameLoaded === "function" && typeof currentIndex === "number") {
      Promise.resolve().then(() => onFrameLoaded(currentIndex));
    }
    return Promise.resolve();
  }

  // Wadouri whole-file stacks (priors / legacy): use the bounded rolling-window
  // warmup so the buffer bar fills to 100% at idle instead of stalling before
  // the tail. Desktop only — mobile keeps the windowed/ceilinged path below to
  // stay within its per-tab RAM budget, and wadors stacks keep the semaphore
  // fan-out (independent per-frame fetches, so high concurrency is a real win).
  const isWadorsStack =
    typeof imageIds[0] === "string" && imageIds[0].startsWith("wadors:");
  if (!isWadorsStack && !isMobile) {
    return prefetchWadouriFramesBounded(imageIds, currentIndex, onFrameLoaded);
  }
  const threshold = Math.min(PREFETCH_WARMUP_FRAMES, imageIds.length);
  return new Promise((resolve) => {
    let settled = 0;
    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      resolve();
    };
    const bump = () => {
      settled += 1;
      if (settled >= threshold) finish();
    };
    // Safety net — even if every prefetch hangs, the engine starts
    // ticking after this timeout. Cornerstone will just demand-load
    // missing frames as the cursor advances.
    setTimeout(finish, PREFETCH_WARMUP_TIMEOUT_MS);

    // currentIndex frame is already loaded by setStack — count it both
    // for the warmup threshold and for the engine's loaded-frame
    // tracker.
    if (typeof onFrameLoaded === "function" && typeof currentIndex === "number") {
      Promise.resolve().then(() => onFrameLoaded(currentIndex));
    }
    bump();

    // First pass: short-circuit any frame that's already in Cornerstone's
    // image cache. This makes revisits to an already-loaded instance
    // instantly report 100% loaded — the throttle queue is for cold
    // frames only.
    for (let i = 0; i < imageIds.length; i++) {
      if (i === currentIndex) continue;
      if (isFrameCached(imageIds[i])) {
        if (typeof onFrameLoaded === "function") onFrameLoaded(i);
        bump();
      }
    }

    // Second pass: enqueue every not-yet-cached frame so the buffer bar fills
    // gradually across the whole track (YouTube-style), notifying as each
    // settles. Priority is split so playback stays smooth while the bar fills:
    //   - Frames within the forward window (maxInitialFrames, mobile) →
    //     HIGH priority: the playback-critical frames the cursor will hit
    //     first. They jump the queue ahead of background work.
    //   - Frames beyond the window → LOW priority: they trickle in behind the
    //     high-priority window (and behind the sliding-window prefetch, which
    //     re-promotes frames ahead of the cursor to HIGH during play). This is
    //     what makes the buffer fill progressively instead of stalling at the
    //     window edge. Peak memory is still bounded by the cornerstone cache
    //     cap, which evicts once full.
    // Desktop (maxInitialFrames = Infinity) warms everything at HIGH priority.
    for (let i = 0; i < imageIds.length; i++) {
      if (i === currentIndex) continue;
      if (isFrameCached(imageIds[i])) continue;
      let priority = "high";
      if (Number.isFinite(maxInitialFrames)) {
        const fwd =
          (((i - currentIndex) % imageIds.length) + imageIds.length) %
          imageIds.length;
        // Hard memory ceiling (mobile): never warm beyond this many frames
        // ahead. Without it, a long cine (e.g. 766 frames) caches the whole
        // instance and reloads the tab on iOS. Frames past the ceiling warm
        // on demand via the sliding window during play; frames behind evict.
        if (fwd > MOBILE_MAX_PREFETCH_FRAMES) continue;
        // Within the ceiling but beyond the high-priority window → low priority
        // (gradual background fill of the buffer bar).
        if (fwd > maxInitialFrames) priority = "low";
      }
      const idx = i;
      const promise = enqueueFramePrefetch(imageIds[i], { priority });
      promise.then(
        () => {
          if (typeof onFrameLoaded === "function") onFrameLoaded(idx);
          bump();
        },
        () => {
          if (typeof onFrameLoaded === "function") onFrameLoaded(idx);
          bump();
        }
      );
    }
    // Safety: if threshold was 1 (single-extra-frame case), resolve now.
    if (threshold <= 1) finish();
  });
};

const wireEngineToBus = (engine) => {
  const handler = (event) => {
    if (event.sourceViewportId === engine.viewportId) return;
    if (event.mode === SYNC_MODES.UNSYNCHRONIZED) return;

    engine._suppressBroadcast = true;
    try {
      if (event.action === "play") {
        // PLAY_SYNC_START aligns frame index + fps to the master; SYNC_PLAYBACK
        // just starts playback at the local fps from the current frame.
        if (event.mode === SYNC_MODES.PLAY_SYNC_START) {
          if (Number.isFinite(event.payload?.frame)) {
            engine.seekTo(
              Math.min(event.payload.frame, engine.totalFrames - 1)
            );
          }
          if (Number.isFinite(event.payload?.fps)) {
            engine.setFps(event.payload.fps);
          }
        }
        if (!engine.isPlaying) engine.play();
      } else if (event.action === "pause") {
        if (engine.isPlaying) engine.pause();
      }
    } finally {
      engine._suppressBroadcast = false;
    }
  };
  return subscribeBroadcast(handler);
};

const cs3DToolsEvents = Enums.Events;

const onRightClick = async (event) => {
  const { element, currentPoints } = event.detail;
  const nearbyToolData = utilities.getAnnotationNearPoint(
    element,
    currentPoints.canvas
  );
  const annotationManager = annotation.state.getAnnotationManager();
  if (nearbyToolData !== null) {
    eventBus.dispatch("contextMenuAdd", {
      show: true,
      mouseEvent: event.detail.event,
      data: nearbyToolData,
      showRemove: true,
    });
  } else {
    eventBus.dispatch("contextMenuAdd", {
      show: true,
      mouseEvent: event.detail.event,
      data: nearbyToolData,
      showRemove: false,
    });
  }
};

const resetContextMenu = (event) => {
  eventBus.dispatch("contextMenuAdd", {
    show: false,
    mouseEvent: null,
    data: null,
    showRemove: false,
  });
};

export const eventBus = {
  on(event, callback) {
    document.addEventListener(event, (e) => callback(e.detail));
  },
  dispatch(event, data) {
    document.dispatchEvent(new CustomEvent(event, { detail: data }));
  },
  remove(event, callback) {
    document.removeEventListener(event, callback);
  },
};

const contextMenuHandleClick = (evt) => {
  const mouseUpEvent = evt.detail.event;
  const isRightClick = mouseUpEvent.which === 3;

  const clickMethodHandler = isRightClick ? onRightClick : resetContextMenu;
  clickMethodHandler(evt);
};

function elementEnabledHandler(evt) {
  const { element } = evt.detail;
  element.addEventListener(cs3DToolsEvents.MOUSE_CLICK, contextMenuHandleClick);

  // element.addEventListener(EVENTS.CAMERA_RESET, resetCrosshairs);

  // eventTarget.addEventListener(
  //   Enums.EVENTS.STACK_VIEWPORT_NEW_STACK,
  //   newStackCallback
  // );
}
function elementDisabledHandler(evt) {
  const { element } = evt.detail;
  // element.removeEventListener(
  //   cs3DToolsEvents.MOUSE_CLICK,
  //   contextMenuHandleClick
  // );

  // element.removeEventListener(EVENTS.CAMERA_RESET, resetCrosshairs);

  // // TODO - consider removing the callback when all elements are gone
  // // eventTarget.removeEventListener(
  // //   EVENTS.STACK_VIEWPORT_NEW_STACK,
  // //   newStackCallback
  // // );
}

eventTarget.addEventListener(
  cornerstone.EVENTS.ELEMENT_ENABLED,
  elementEnabledHandler
);

eventTarget.addEventListener(
  cornerstone.EVENTS.ELEMENT_DISABLED,
  elementDisabledHandler
);

const RENDERING_ENGINE_ID = getEnv("REACT_APP_RENDERING_ENGINE_ID");

const TOOL_GROUP_ID = getEnv("REACT_APP_TOOL_GROUP_ID");
const MPR_TOOL_GROUP_ID = getEnv("REACT_APP_MPR_TOOL_GROUP_ID");
const VOLUME_3D_TOOL_GROUP_ID = getEnv("REACT_APP_VOLUME_3D_TOOL_GROUP_ID");
const VOI_SYNC_ID = getEnv("REACT_APP_VOI_SYNCHRONIZER_ID");

export const getRenderingEngineId = () => {
  return RENDERING_ENGINE_ID;
};

export const renderViewports = async () => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  // Never renderingEngine.render() — see _renderLoadedViewports.
  _renderLoadedViewports(renderingEngine);
};

export const getViewportInput = (element, viewportIndex) => {
  return {
    viewportId: "viewport_" + viewportIndex,
    type: ViewportType.STACK,
    element: element,
    defaultOptions: {
      orientation: cornerstone.Enums.OrientationAxis.AXIAL,
      displayArea: {
        storeAsInitialCamera: false,
      },
    },
  };
};

export const activateViewport = async (element, viewportIndex) => {
  //   viewports.forEach((viewport) => {
  const viewportInput = getViewportInput(element, viewportIndex);
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  // console.log("BEFORE ENABLING VIEWPORT")
  renderingEngine.enableElement(viewportInput);
  // addViewportToToolGroup(TOOL_GROUP_ID, viewportInput.viewportId, RENDERING_ENGINE_ID);
  //   });
};

export const resizeViewport = async () => {
  try {
    const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
    // resize(false) recalculates canvas dimensions and sx offsets WITHOUT calling
    // the internal render() that resize(true) would trigger.  resize(true) adds
    // ALL viewport IDs to _needsRender — including empty viewports — so when the
    // rAF fires, empty viewports blit from the wrong off-screen region and show
    // another viewport's image.  By using resize(false) + explicit renderViewports()
    // we control exactly which viewports are scheduled for the next blit.
    renderingEngine.resize(false, true);
    _renderLoadedViewports(renderingEngine);
  } catch (error) {
    console.log(error);
  }
};

/**
 * Synchronous version of resizeViewport.
 * Uses cornerstone.getRenderingEngine directly (no await) so it can be
 * called from useLayoutEffect to resize canvases before the browser paints.
 */
export const resizeViewportSync = () => {
  try {
    const renderingEngine = cornerstone.getRenderingEngine(RENDERING_ENGINE_ID);
    if (renderingEngine) {
      renderingEngine.resize(false, true);
      _renderLoadedViewports(renderingEngine);
    }
  } catch (error) {
    console.log(error);
  }
};

let _layoutResizeRafId = null;
let _layoutResizePendingFrames = 0;

/**
 * Resizes the Cornerstone canvases AFTER the browser has applied a pending
 * layout change.
 *
 * Callers that change a viewport's CSS size (MPR double-click maximize, MPR
 * plane switch, 3D on/off) used to call resizeViewport() straight from the
 * click handler.  That runs BEFORE React commits the new sizes, so
 * `canvas.clientWidth` is still the OLD width and cornerstone's
 * `_resizeVTKViewports` sees "displayed size === rendered size" and skips the
 * viewport entirely.  The canvas then keeps its old pixel dimensions inside a
 * much wider CSS box and the browser scales the stale bitmap up — the image
 * looks stretched.
 *
 * Resizing on the next animation frames fixes that: styles are committed and
 * layout is up to date by then.  Several frames are used because cornerstone
 * bails out of a resize while a render is already queued
 * (`if (this._animationFrameSet) return;`), so a single call can silently be
 * dropped.  Once the canvas matches its box the extra passes are no-ops.
 */
export const resizeViewportsAfterLayout = (frames = 3) => {
  if (typeof window === "undefined") return;

  _layoutResizePendingFrames = Math.max(_layoutResizePendingFrames, frames);
  if (_layoutResizeRafId !== null) return;

  const step = () => {
    _layoutResizeRafId = null;
    _layoutResizePendingFrames -= 1;
    resizeViewportSync();
    if (_layoutResizePendingFrames > 0) {
      _layoutResizeRafId = requestAnimationFrame(step);
    }
  };

  _layoutResizeRafId = requestAnimationFrame(step);
};

// Viewports whose on-screen <canvas> BITMAP size no longer matches the size the
// canvas is displayed at.  Mirrors the check cornerstone itself does in
// ContextPoolRenderingEngine._resizeVTKViewports, so callers can cheaply tell
// "nothing to do" from "a resize is genuinely pending".
const _viewportsNeedingCanvasResize = (renderingEngine) => {
  const devicePixelRatio = window.devicePixelRatio || 1;
  return renderingEngine.getViewports().filter((vp) => {
    const canvas = vp.canvas;
    if (!canvas) return false;
    const displayedWidth = Math.round(canvas.clientWidth * devicePixelRatio);
    const displayedHeight = Math.round(canvas.clientHeight * devicePixelRatio);
    // A hidden/collapsed viewport has no displayed size to match.
    if (displayedWidth === 0 || displayedHeight === 0) return false;
    return displayedWidth !== canvas.width || displayedHeight !== canvas.height;
  });
};

// Drops cornerstone's queued render frame WITHOUT clearing `_needsRender`, so the
// viewports it had flagged still get drawn by the next render call.
// Needed because `_resizeVTKViewports` bails out completely while a render is
// queued (`if (this._animationFrameSet) return;`) — leaving that flag set would
// silently discard the resize we are about to ask for.
const _cancelQueuedRender = (renderingEngine) => {
  if (renderingEngine._animationFrameHandle != null) {
    window.cancelAnimationFrame(renderingEngine._animationFrameHandle);
  }
  renderingEngine._animationFrameHandle = null;
  renderingEngine._animationFrameSet = false;
};

// Flags the loaded viewports and draws them NOW instead of on the next
// animation frame.  `renderViewports()` only ever schedules a rAF, and it is
// that render pass — not resize() — which resizes the on-screen canvas bitmap
// (see _copyToOnscreenCanvas → updateCanvasSizeAndAspectRatio).
const _renderLoadedViewportsNow = (renderingEngine) => {
  _renderLoadedViewports(renderingEngine);
  if (typeof renderingEngine._renderFlaggedViewports !== "function") {
    // Unexpected cornerstone build: leave the already-scheduled rAF in place.
    return;
  }
  _cancelQueuedRender(renderingEngine);
  renderingEngine._renderFlaggedViewports();
};

/**
 * Fully synchronous resize + redraw, intended to be called from a
 * useLayoutEffect — i.e. after React has committed a layout change but BEFORE
 * the browser paints it.
 *
 * Why this exists (the MPR double-click "stretch flash"):
 * cornerstone's `resize()` only updates `viewport.sWidth/sHeight` and the
 * off-screen WebGL buffers.  The on-screen `<canvas>` bitmap (`canvas.width` /
 * `canvas.height`) is resized inside `_copyToOnscreenCanvas()`, which runs as
 * part of a RENDER — and every cornerstone render is deferred to a
 * requestAnimationFrame.  Meanwhile the canvas is styled `width/height: 100%`,
 * so the moment a pane's CSS box grows the browser stretches the old, smaller
 * bitmap across the new, larger box (with `image-rendering: pixelated`).  That
 * stale frame is the visible stretch; it disappears once the deferred render
 * finally repaints at the correct size.
 *
 * Doing the resize AND the render synchronously before the first paint means
 * the new layout is never shown with a stale bitmap.
 *
 * No-ops when every canvas already matches its displayed size, so it is cheap
 * to call on every layout change.
 */
export const resizeViewportsImmediate = () => {
  try {
    const renderingEngine = cornerstone.getRenderingEngine(RENDERING_ENGINE_ID);
    if (!renderingEngine) return;
    if (_viewportsNeedingCanvasResize(renderingEngine).length === 0) return;

    _cancelQueuedRender(renderingEngine);
    renderingEngine.resize(false, true);
    _renderLoadedViewportsNow(renderingEngine);
  } catch (error) {
    console.log(error);
  }
};

export const rerenderViewport = async () => {
  try {
    const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
    // renderingEngine.resize(true, true);
    // Never renderingEngine.render() — see _renderLoadedViewports.
    _renderLoadedViewports(renderingEngine);
  } catch (error) {
    console.log(error);
  }
};


// Returns the IDs of all viewports that have content to render.
// Skipping empty stack viewports prevents the Tiled rendering engine
// from blitting stale or wrong off-screen framebuffer content onto their
// (resize-cleared) canvases — which is the mechanism that caused an empty
// viewport_1 to display viewport_0's image after MPR exit.
const _getViewportIdsToRender = (renderingEngine) => {
  return renderingEngine.getViewports()
    .filter((vp) => {
      // Thumbnails always render (they manage their own load cycle).
      if (vp.id.startsWith('thumbnail')) return true;
      // For all other viewports, only render if there is an image to show.
      return vp.getCurrentImageId?.() !== undefined;
    })
    .map((vp) => vp.id);
};

// Renders only the viewports that hold an image.  ALWAYS use this instead of
// renderingEngine.render(): render() flags every enabled viewport, and the
// ContextPool engine then blits the shared off-screen WebGL canvas — which
// still holds the last rendered image — onto empty viewports, so an empty
// viewport shows a stretched copy of a loaded one behind the
// "drag a stack here" message.
const _renderLoadedViewports = (renderingEngine) => {
  if (!renderingEngine) return;
  const idsToRender = _getViewportIdsToRender(renderingEngine);
  if (idsToRender.length > 0) {
    renderingEngine.renderViewports(idsToRender);
  }
};

export const performResize = async (immediate = false) => {
    try {
      const beforeResizePositionPresentations = new Map();
      const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
      let viewports = await getAllViewports();
      viewports = viewports.filter(
        (viewport) =>
          viewport.getCurrentImageId() !== undefined &&
          viewport.type !== "orthographic" && !viewport.id.startsWith("thumbnail")
      );


      // Store the current position presentations for each viewport.
      viewports.forEach((viewport) => {
        const presentation = _getPositionPresentation(viewport);

        delete presentation.viewReference?.sliceIndex;
        beforeResizePositionPresentations.set(viewport.id, presentation);
      });

      // Resize canvas dimensions and sx offsets without triggering the internal
      // render() that resize(true) would schedule for ALL viewports.
      renderingEngine.resize(false, true);

      // Only render viewports that actually have images.  renderingEngine.render()
      // marks ALL viewports (including empty ones) for the next rAF blit.  The
      // Tiled engine can then copy stale off-screen content onto an empty
      // viewport's resize-cleared canvas, making it appear to show another
      // viewport's image.  Using renderViewports() with only loaded viewports
      // prevents any blit to empty viewports — their canvases stay black.
      _renderLoadedViewports(renderingEngine);

      // Restore the position presentations for each viewport.
      viewports.forEach((viewport) => {
        const positionPresentation = beforeResizePositionPresentations.get(viewport.id);
        if(positionPresentation){
          _setPositionPresentation(viewport, {
            positionPresentation,
          });
        }
      });

      // Re-render only loaded viewports after position restore.
      _renderLoadedViewports(renderingEngine);
    } catch (e) {
      // This can happen if the resize is too close to navigation or shutdown
      console.warn('Caught resize exception', e);
    }
};

const _getPositionPresentation = (viewport) => {
  if (!viewport) {
    return;
  }

  return {
    viewportType: viewport.type,
    viewReference: viewport.getViewReference(),
    viewPresentation: viewport.getViewPresentation({ pan: true, zoom: true }),
    viewportId: viewport.id,
  };
}

const _setPositionPresentation = (viewport, viewportPositionPresentation) => {
  const { positionPresentation } = viewportPositionPresentation;
  const viewRef = positionPresentation?.viewReference;
  if (viewRef) {
    viewport.setViewReference(viewRef);
  }

  const viewPresentation = positionPresentation?.viewPresentation;
  if (viewPresentation) {
    viewport.setViewPresentation(viewPresentation);
  }
}

export const getViewport = async (viewportIndex) => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  //renderingEngine.resize(true, true);
  const viewports = renderingEngine.getViewport("viewport_" + viewportIndex);
  return viewports;
};

export const getViewportByIdAndRenderingEngine = (viewportIndex, renderingEngineId) => {
  const renderingEngine = cornerstone.getRenderingEngine(renderingEngineId);
  const viewport = renderingEngine.getViewport(viewportIndex);
  return viewport;
};

export const getViewportByIdAndDefRenderingEngine = (viewportIndex) => {
  const renderingEngine = cornerstone.getRenderingEngine(RENDERING_ENGINE_ID);
  const viewport = renderingEngine.getViewport(viewportIndex);
  return viewport;
};

export const enableViewport = (viewport) => {
  const renderingEngine = cornerstone.getRenderingEngine(RENDERING_ENGINE_ID);
  renderingEngine.enableElement(viewport);
};



export const getAllViewports = async () => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  return renderingEngine.getViewports();
};

/**
 * Get all active viewports excluding thumbnail viewports
 * Useful for applying filters and enhancements to main viewing area only
 */
export const getPanelViewports = async () => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const viewports = renderingEngine.getViewports();
  return viewports.filter((viewport) => !viewport.id.startsWith("thumbnail"));
};

export const createVTKImageData = ({
  origin,
  direction,
  dimensions,
  spacing,
  bitsAllocated,
  numberOfComponents,
  numVoxels,
}) => {
  let pixelArray;
  switch (bitsAllocated) {
    case 8:
      pixelArray = new Uint8Array(numVoxels * numberOfComponents);
      break;
    case 16:
      pixelArray = new Float32Array(numVoxels * numberOfComponents);
      break;
    case 24:
      pixelArray = new Uint8Array(numVoxels * 3 * numberOfComponents);
      break;
    default:
  }
  const scalarArray = vtkDataArray.newInstance({
    name: "Pixels",
    numberOfComponents: numberOfComponents,
    values: pixelArray,
  });
  const imageData = vtkImageData.newInstance();
  imageData.setDimensions(dimensions);
  imageData.setSpacing(spacing);
  imageData.setDirection(direction);
  imageData.setOrigin(origin);
  // const rgb_array = imageData.getPointData().getArray(0);
  // console.log(imageData.getPointData());
  // imageData.getPointData().setScalars(rgb_array);
  imageData.getPointData().setScalars(scalarArray);
  return imageData;
};

export const updateVTKImageDataFromCornerstoneImageForHeatmap = (
  image,
  imagePlaneModule,
  vtkImageData
) => {
  let origin = imagePlaneModule.imagePositionPatient;
  if (origin === null) {
    origin = [0, 0, 0];
  }
  vtkImageData.setOrigin(origin);
  const pixelData = image.getPixelData();
  const scalars = vtkImageData.getPointData().getScalars();
  const scalarData = scalars.getData();
  if (image.rgba || isRgbaSourceRgbDestJsx(pixelData, scalarData)) {
    if (!image.rgba) {
      console.warn("rgba not specified but data looks rgba ish", image);
    }
    const numPixels = pixelData.length / 4;
    let rgbIndex = 0;
    let index = 0;
    let max = 0;
    let alphaIndex = 3;
    for (let i = 0; i < numPixels; i++) {
      if (max <= pixelData[alphaIndex]) max = pixelData[alphaIndex];
      alphaIndex += 4;
    }

    for (let i = 0; i < numPixels; i++) {
      // scalarData[index++] = pixelData[rgbIndex] >= 120 && pixelData[rgbIndex] <= 220 ? pixelData[rgbIndex++] * 0.7 : pixelData[rgbIndex] > 220 ? pixelData[rgbIndex++] * 0.8 : pixelData[rgbIndex++] * 0;
      // scalarData[index++] = pixelData[rgbIndex] >= 140 && pixelData[rgbIndex] <= 220 ? pixelData[rgbIndex++] * 0.7 : pixelData[rgbIndex] > 220 ? pixelData[rgbIndex++] * 0.6 : pixelData[rgbIndex++] * 0.2;

      let per = pixelData[rgbIndex + 3] / max;
      // scalarData[index++] = pixelData[rgbIndex++] > 20 ? pixelData[rgbIndex] * per : 0;
      // scalarData[index++] = pixelData[rgbIndex++] > 20 ? pixelData[rgbIndex] * per : 0;
      // scalarData[index++] = pixelData[rgbIndex++] > 20 ? pixelData[rgbIndex] * per : 0;
      scalarData[index++] = pixelData[rgbIndex++] * per;
      scalarData[index++] = pixelData[rgbIndex++] * per;
      scalarData[index++] = pixelData[rgbIndex++] * per;
      // pixelData[rgbIndex] > 250
      // ? pixelData[rgbIndex++] * 0.3
      // : pixelData[rgbIndex++] * 0;
      // // scalarData[index++] = pixelData[rgbIndex++];
      // scalarData[index++] =
      // pixelData[rgbIndex] > 250
      // ? pixelData[rgbIndex++] * 0
      // : pixelData[rgbIndex++] * 0;
      // scalarData[index++] = 0;
      // rgbIndex++;
      rgbIndex++;
      // rgbIndex++;
      // rgbIndex++
    }
  } else {
    scalarData.set(pixelData);
  }
  vtkImageData.modified();
  return vtkImageData;
};

const updateVTKImageDataFromCornerstoneImageForOutline = (
  image,
  imagePlaneModule,
  vtkImageData
) => {
  let origin = imagePlaneModule.imagePositionPatient;
  if (origin === null) {
    origin = [0, 0, 0];
  }
  vtkImageData.setOrigin(origin);
  const pixelData = image.getPixelData();
  const scalars = vtkImageData.getPointData().getScalars();
  const scalarData = scalars.getData();
  if (image.rgba || isRgbaSourceRgbDestJsx(pixelData, scalarData)) {
    if (!image.rgba) {
      console.warn("rgba not specified but data looks rgba ish", image);
    }
    const numPixels = pixelData.length / 4;
    let rgbIndex = 0;
    let index = 0;
    for (let i = 0; i < numPixels; i++) {
      scalarData[index++] = pixelData[rgbIndex++]; //*per;
      scalarData[index++] = pixelData[rgbIndex++]; //*per;
      scalarData[index++] = pixelData[rgbIndex++]; //*per;

      // scalarData[index++] = pixelData[rgbIndex] >= 180 && pixelData[rgbIndex] <= 220 ? pixelData[rgbIndex++] * 0.7 : pixelData[rgbIndex] > 220 ? pixelData[rgbIndex++] * 0.8 : pixelData[rgbIndex++] * 0;
      // scalarData[index++] = 0;
      // scalarData[index++] = 0;
      // scalarData[index++] = pixelData[rgbIndex] >= 120 && pixelData[rgbIndex] <= 220 ? pixelData[rgbIndex++] * 0.7 : pixelData[rgbIndex] > 220 ? pixelData[rgbIndex++] * 0.6 : pixelData[rgbIndex++] * 0.25;
      // scalarData[index++] = pixelData[rgbIndex] > 160 ? pixelData[rgbIndex++] * 0.8: pixelData[rgbIndex++] * 0.25;
      // scalarData[index++] = pixelData[rgbIndex] > 240 ? pixelData[rgbIndex++] * 0.4 : pixelData[rgbIndex++] * 0.2;
      // scalarData[index++] = 0;
      // rgbIndex++;
      rgbIndex++;
      // rgbIndex++;
      // rgbIndex++
    }
  } else {
    scalarData.set(pixelData);
  }
  vtkImageData.modified();
  return vtkImageData;
};

/**
 * Pass RGBA pixel data directly to VTK image data, preserving the per-pixel
 * alpha channel from the PNG. Transparent pixels (alpha=0) will be invisible
 * and colored pixels will show at their native alpha.
 * @param {number} [alphaScale=1.0] - Factor to scale all alpha values by
 *   (e.g. 0.5 makes the overlay 50% see-through). 1.0 = use PNG's original alpha.
 */
export const updateVTKImageDataRGBA = (
  image,
  imagePlaneModule,
  vtkImageData,
  alphaScale = 0.7
) => {
  let origin = imagePlaneModule.imagePositionPatient;
  if (origin === null) {
    origin = [0, 0, 0];
  }
  vtkImageData.setOrigin(origin);
  const pixelData = image.getPixelData();
  const scalars = vtkImageData.getPointData().getScalars();
  const scalarData = scalars.getData();

  // Copy RGBA data directly, optionally scaling alpha
  if (alphaScale === 1.0) {
    scalarData.set(pixelData);
  } else {
    const len = pixelData.length;
    for (let i = 0; i < len; i += 4) {
      scalarData[i] = pixelData[i];       // R
      scalarData[i + 1] = pixelData[i + 1]; // G
      scalarData[i + 2] = pixelData[i + 2]; // B
      scalarData[i + 3] = Math.round(pixelData[i + 3] * alphaScale); // A (scaled)
    }
  }

  vtkImageData.modified();
  return vtkImageData;
};

export const loadOutline = async (
  outlineStack,
  buttonClicked,
  activeViewportIndex
) => {
  try {
    const activeViewport = await getViewport(activeViewportIndex);
    if (!activeViewport) {
      return;
    }
    // Nothing to overlay on an empty viewport — and rendering one blits stale
    // off-screen content onto it (see _renderLoadedViewports).
    if (activeViewport.getCurrentImageId?.() === undefined) {
      return;
    }

    if (!buttonClicked) {
      if (activeViewport.getActor("outline")) {
        activeViewport.removeActors(["outline"]);
      }
      activeViewport.render();
      return;
    }

    if (activeViewport.getActor("outline")) {
      activeViewport.removeActors(["outline"]);
    }

    const loadedImageData = activeViewport.getImageData();
    if (!loadedImageData) {
      return;
    }

    const imageIds = outlineStack;
    if (!Array.isArray(imageIds) || imageIds.length === 0) {
      return;
    }

    const currentIndex = activeViewport.getCurrentImageIdIndex();
    const safeIndex = Math.min(currentIndex, imageIds.length - 1);
    const image = imageIds[safeIndex];
    if (image === null || image === undefined || image === "") return;

    const pngData = await loadAndCacheImage(image);

    const imageData = activeViewport.getImageDataMetadata(pngData);
    // Force numberOfComponents to 3 (RGB) since our pixel processing
    // extracts RGB from RGBA source data. bitsAllocated defaults to 8 for PNGs.
    // Use 4 components (RGBA) so the PNG's per-pixel alpha is preserved.
    // Transparent background (alpha=0) stays invisible; colored pixels show
    // at their native alpha — no global setOpacity needed.
    const vtkImgData = createVTKImageData({
      origin: imageData.origin,
      direction: imageData.direction,
      dimensions: imageData.dimensions,
      spacing: loadedImageData.spacing,
      bitsAllocated: imageData.bitsAllocated || 8,
      numberOfComponents: 4,
      numVoxels: imageData.numVoxels,
    });

    const updatedvtkImageData = updateVTKImageDataRGBA(
      pngData,
      imageData.imagePlaneModule,
      vtkImgData
    );

    const actor = activeViewport.createActorMapper(updatedvtkImageData);

    activeViewport.addActor({ uid: "outline", actor, slabThickness: 1 });
    activeViewport.render();
  } catch (error) {
    console.error("[loadOutline] Error loading outline overlay:", error);
  }
};

export const loadHeatMap = async (
  heatmapStack,
  buttonClicked,
  activeViewportIndex
) => {
  try {
    const activeViewport = await getViewport(activeViewportIndex);
    if (!activeViewport) {
      return;
    }
    // Nothing to overlay on an empty viewport — and rendering one blits stale
    // off-screen content onto it (see _renderLoadedViewports).
    if (activeViewport.getCurrentImageId?.() === undefined) {
      return;
    }

    if (!buttonClicked) {
      if (activeViewport.getActor("heatmap")) {
        activeViewport.removeActors(["heatmap"]);
      }
      activeViewport.render();
      return;
    }

    if (activeViewport.getActor("heatmap")) {
      activeViewport.removeActors(["heatmap"]);
    }

    const loadedImageData = activeViewport.getImageData();
    if (!loadedImageData) {
      return;
    }

    const imageIds = heatmapStack;
    if (!Array.isArray(imageIds) || imageIds.length === 0) {
      return;
    }

    const currentIndex = activeViewport.getCurrentImageIdIndex();
    const safeIndex = Math.min(currentIndex, imageIds.length - 1);
    const image = imageIds[safeIndex];
    if (image === null || image === undefined || image === "") return;

    const pngData = await loadAndCacheImage(image);

    const imageData = activeViewport.getImageDataMetadata(pngData);
    // Force numberOfComponents to 3 (RGB) since our pixel processing
    // extracts RGB from RGBA source data. bitsAllocated defaults to 8 for PNGs.
    // Use 4 components (RGBA) so the PNG's per-pixel alpha is preserved.
    // Transparent background (alpha=0) stays invisible; colored heatmap
    // pixels show at their native alpha — no global setOpacity needed.
    const vtkImgData = createVTKImageData({
      origin: imageData.origin,
      direction: imageData.direction,
      dimensions: imageData.dimensions,
      spacing: loadedImageData.spacing,
      bitsAllocated: imageData.bitsAllocated || 8,
      numberOfComponents: 4,
      numVoxels: imageData.numVoxels,
    });

    const updatedvtkImageData = updateVTKImageDataRGBA(
      pngData,
      imageData.imagePlaneModule,
      vtkImgData
    );

    const actor = activeViewport.createActorMapper(updatedvtkImageData);

    activeViewport.addActor({ uid: "heatmap", actor, slabThickness: 1 });
    activeViewport.render();
  } catch (error) {
    console.error("[loadHeatMap] Error loading heatmap overlay:", error);
  }
};

export const loadActiveViewport = async (
  thumbnailIndex,
  activeViewportIndex,
  setScale,
  setInitialVOIRange,
  setInitialVOI,
  isSetLastImage,
  setCurrentImage,
  currentImage,
  loadAIOutlineAndHeatmap,
  imageIndex
) => {
  const thumbnailData = await getThumbnailViewport(thumbnailIndex);
  if (!thumbnailData) return;
  const imageIds = thumbnailData.imageIds;
  const activeViewport = await getViewport(activeViewportIndex);

  if (!activeViewport) return;
  activeViewport.removeAllActors();
  cornerstone.metaData.addProvider(
    (type, imageId) =>
      hardcodedMetaDataProvider(type, imageId, thumbnailData.imageIds),
    10000
  );

  // Multi-frame detection: a "stack" with a single instance may still be a
  // multi-frame DICOM. If so, expand to N frame image IDs (wadouri ?frame=N)
  // so the existing stack pipeline can render each frame as if it were its
  // own image. The wadouri loader fetches the underlying DICOM once and
  // extracts each frame from the cached parsed dataset.
  let imageIdsForViewport = thumbnailData.getImageIds();
  let multiFrameContext = null;
  if (imageIdsForViewport.length === 1) {
    try {
      const detection = await detectMultiFrame(imageIdsForViewport[0]);
      if (detection.isMultiFrame) {
        const baseImageId = thumbnailData.getImageIds()[0];
        // Prefer backend-served per-frame WADO-RS URLs for both desktop and
        // mobile. UIDs are extracted from the registry (keyed by whichever URL
        // scheme was used at study load — wadors: on desktop, dicomweb: on
        // mobile). If UIDs are unavailable, fall back to wadouri:?frame=N.
        // Mobile uses the same JWT-gated /frames/N endpoint as desktop because
        // the user is authenticated; only the THUMBNAIL keeps the dicomweb: URL
        // (set in generateStudyData) to avoid a heavy full-DICOM download there.
        const isWadorsBase = baseImageId.startsWith("wadors:");
        // Priors-loaded instances are flagged preferWadouriFrames because their
        // per-frame WADO-RS fetches aren't authorized under the priors session
        // JWT. Force the wadouri ?frame=N expansion for them by suppressing the
        // UID lookup — otherwise the wadors rebuild below 401s the /metadata
        // fetch and setStack crashes reading `samplesPerPixel` off undefined.
        const baseMeta = getImageMetadata(baseImageId);
        const preferWadouriFrames = baseMeta?.preferWadouriFrames === true;
        // Always try the registry lookup regardless of URL scheme — the registry
        // is keyed by the stack's baseImageId (dicomweb: on mobile, wadors: on
        // desktop) so extractInstanceUids works for both.
        const uids = preferWadouriFrames ? null : extractInstanceUids(baseImageId);
        const wadorsIds = uids
          ? buildWadorsFrameImageIds({
              baseImageId,
              ...uids,
              numberOfFrames: detection.numberOfFrames,
            })
          : null;
        if (wadorsIds) {
          imageIdsForViewport = wadorsIds;
          // When the base URL was a dicomweb: URL (mobile), the metadata registry
          // is keyed by that dicomweb: URL — not by the wadors frame URLs we just
          // built. Seed the registry under the wadors frame-1 URL so that
          // ensureWadorsMetadataReady (line below) can locate the SOP metadata and
          // trigger the /metadata fetch before setStack.
          if (!isWadorsBase) {
            registerImageMetadata(wadorsIds[0], {
              ...uids,
              numberOfFrames: detection.numberOfFrames,
            });
          }
        } else {
          imageIdsForViewport = buildFrameImageIds(
            baseImageId,
            detection.numberOfFrames
          );
        }
        multiFrameContext = {
          baseImageId,
          numberOfFrames: detection.numberOfFrames,
          // Source FPS is resolved later (at engine creation), once the
          // wadors /metadata fetch + setStack have run — resolving it here
          // would run before the cine tags exist and always yield the default.
          // UIDs are present when the wadors path succeeded — used for the
          // sliding-window prefetch callback wired into the playback engine.
          ...(uids || {}),
        };
      }
    } catch (err) {
      console.debug(
        "[MultiFrame] Detection failed; using single-image stack.",
        err
      );
    }
  }

  // Mixed-series two-axis detection (Phase B). For viewports whose stack
  // has multiple instances and at least one of them is multi-frame, build
  // a per-instance frame map. The viewport stays in OUTER mode (slot per
  // instance) by default; cine play swaps it into INNER mode for the
  // active instance's frames. Single-instance multi-frame (handled above)
  // and pure single-frame stacks skip this block.
  let mixedSeriesContext = null;
  if (imageIdsForViewport.length > 1) {
    const outerStack = imageIdsForViewport;
    const instanceFrameStacks = new Map();
    for (let i = 0; i < outerStack.length; i++) {
      const meta = getImageMetadata(outerStack[i]);
      if (
        !meta ||
        typeof meta.numberOfFrames !== "number" ||
        meta.numberOfFrames <= 1
      ) {
        continue;
      }
      // Priors instances stay on the wadouri ?frame=N path (see the
      // single-instance block above); a wadors rebuild here would 401 under
      // the priors JWT. wadouri needs no UIDs — it extracts frames from the
      // one cached full-file DICOM.
      let frames;
      if (meta.preferWadouriFrames === true) {
        frames = buildFrameImageIds(outerStack[i], meta.numberOfFrames);
      } else if (meta.studyUid && meta.seriesUid && meta.sopUid) {
        frames = buildWadorsFrameImageIds({
          baseImageId: outerStack[i],
          studyId: meta.studyId,
          studyUid: meta.studyUid,
          seriesUid: meta.seriesUid,
          sopUid: meta.sopUid,
          numberOfFrames: meta.numberOfFrames,
        });
      } else {
        continue;
      }
      if (frames) instanceFrameStacks.set(i, frames);
    }
    if (instanceFrameStacks.size > 0) {
      mixedSeriesContext = { outerStack, instanceFrameStacks };
    }
  }

  // Dispose any engine from the previous instance shown in this viewport.
  // If the new instance is also multi-frame, a fresh engine is registered
  // below.
  disposeBusSubscription(activeViewport.id);
  disposeEngine(activeViewport.id);
  disposeMixedSeriesState(activeViewport.id);
  disposeSingleFrameWheelHandler(activeViewport.id, activeViewport.element);

  const imageIndexVal = isSetLastImage ? imageIdsForViewport.length - 1 : imageIndex && imageIndex > 0 ? imageIndex : 0;

  // Defensive filter — Cornerstone's auto-registered wadouri metaData
  // provider calls parseImageId(imageId) without checking that imageId is
  // a string, so any undefined entry in the stack crashes setStack with
  // "Cannot read properties of undefined (reading 'indexOf')". Drop the
  // bad entries and log so we can hunt down the upstream source.
  const beforeLen = imageIdsForViewport.length;
  imageIdsForViewport = imageIdsForViewport.filter(
    (id) => typeof id === "string" && id.length > 0
  );
  if (imageIdsForViewport.length === 0) {
    console.warn("[MultiFrame] viewport stack is empty after filtering — aborting setStack");
    return;
  }

  // Ensure any wadors:.../frames/N slots have their /metadata registered
  // with the wadors loader before setStack. The thumbnail viewport
  // usually already triggered this; the await on the cached promise is a
  // no-op when it has resolved, and a brief wait when the user is faster
  // than the network. Single-frame (wadouri) slots are skipped.
  await ensureWadorsMetadataReady(imageIdsForViewport);

  const safeImageIndex = Math.min(
    Math.max(0, imageIndexVal),
    imageIdsForViewport.length - 1
  );
  await activeViewport.setStack(imageIdsForViewport, safeImageIndex);
  const currentImageId = imageIndexVal;

  // Enable prefetch to preload adjacent images for smooth scrolling, unless
  // the user paused this series' preload from its thumbnail.
  bindViewportStackPrefetch(activeViewport.element, thumbnailIndex);

  try {
    activeViewport.render();
  } catch (error) {
    console.log(error);
  }
  // Backstop for silent WebGL context loss: when too many studies are open the
  // browser evicts this tab's context and vtk renders BLACK without throwing.
  // Detect it post-render and offer the guided reload instead of a dead viewport.
  try {
    checkEngineContextLost(
      activeViewport.getRenderingEngine && activeViewport.getRenderingEngine()
    );
  } catch (_) { /* never block load on the recovery check */ }
  if(currentImageId == currentImage)
    loadAIOutlineAndHeatmap();

  setCurrentImage(currentImageId);
  setInitialVOIRange(activeViewport.getProperties().voiRange);
  if(setInitialVOI)
    setInitialVOI(activeViewport.getProperties().voiRange);
  loadDefaultWindowLevelToolGroup(activeViewport, activeViewportIndex);

  const toolGroup = getToolGroupManager(TOOL_GROUP_ID);
  toolGroup.addViewport(activeViewport.id, RENDERING_ENGINE_ID);

  // If this load surfaced a multi-frame instance, spin up a playback engine
  // bound to this viewport. The engine is paused until something (toolbar,
  // keyboard shortcut, AutoStart setting) triggers play().
  // Engine state is intentionally NOT mirrored into React state — that
  // caused a re-render loop with the useEffects that re-trigger
  // loadActiveViewport. Consumers (Phase 3 toolbar, keyboard handlers) read
  // the engine directly via getEngine(viewportId) and subscribe for updates.
  if (multiFrameContext) {
    // Resolve the source frame rate HERE — after ensureWadorsMetadataReady
    // and setStack above — so the cine tags are registered/parsed. Querying
    // imageIdsForViewport[0] (frame 1) hits the wadors metaDataManager entry
    // for un-split instances; resolveInitialFps falls back to the default
    // when no rate tag is present. (Doing this at multiFrameContext build
    // time ran before the metadata existed, so it always got the default.)
    const initialFps = resolveInitialFps(imageIdsForViewport[0], null);
    const engine = new MultiFramePlaybackEngine({
      viewportId: activeViewport.id,
      totalFrames: multiFrameContext.numberOfFrames,
      fps: initialFps,
      initialFrame: imageIndexVal,
      onFrame: (frame) => {
        try {
          activeViewport.setImageIdIndex(frame);
          activeViewport.render();
        } catch (err) {
          console.debug("[MultiFrame] frame render failed:", err);
        }
      },
    });
    registerEngine(activeViewport.id, engine);
    busSubscriptions.set(activeViewport.id, wireEngineToBus(engine));
    // Mirror the real cache into the engine's loaded-set so the buffer bar
    // shows what's actually cached (filling as frames prefetch, receding on
    // eviction) rather than a monotonic count.
    startCacheSync(activeViewport.id, engine, imageIdsForViewport);

    // Sliding-window prefetch: as the playback cursor advances, pre-warm
    // PREFETCH_LOOK_AHEAD frames at high priority so the network never
    // becomes the bottleneck during cine.
    if (multiFrameContext.sopUid) {
      // wadors path: rebuild per-frame WADO-RS URLs from the instance UIDs.
      engine.setSlidingWindowCallback((frame0) => {
        advancePrefetchWindow({
          studyId:        multiFrameContext.studyId,
          studyUid:       multiFrameContext.studyUid,
          seriesUid:      multiFrameContext.seriesUid,
          sopUid:         multiFrameContext.sopUid,
          numberOfFrames: multiFrameContext.numberOfFrames,
          currentFrame:   frame0 + 1, // engine 0-indexed → WADO-RS 1-indexed
        });
      });
    } else {
      // wadouri path (priors / legacy): no UID-derived frame URLs, so slide the
      // window over the actual ?frame=N imageIds — mirrors the mixed-series
      // slider. Without this the priors cine had NO prefetch-ahead during
      // playback, so it ran into cold frames the demand-load overlay never
      // buffered (playback controls skip-and-continue during play). Warming the
      // window ahead of the cursor keeps the cine smooth and lets the overlay
      // surface only for genuine gaps (e.g. a fast scrub past the buffer).
      const frames = imageIdsForViewport;
      engine.setSlidingWindowCallback((frame0) => {
        const start = Math.max(0, frame0 - PREFETCH_LOOK_BEHIND);
        const end = Math.min(frames.length - 1, frame0 + PREFETCH_LOOK_AHEAD);
        for (let i = start; i <= end; i++) {
          const id = frames[i];
          if (!id) continue;
          const idx = i;
          if (isFrameCached(id)) {
            engine.notifyFrameLoaded(idx);
            continue;
          }
          enqueueFramePrefetch(id, { priority: "high" }).then(
            () => engine.notifyFrameLoaded(idx),
            () => {}
          );
        }
      });
    }

    // Wheel during cine: pause so Cornerstone's StackScrollTool can
    // scrub frames normally. Without this the engine's rAF tick keeps
    // overriding the scroll position on the very next frame, making
    // the wheel feel broken.
    const wheelPauseHandler = () => {
      const eng = getEngine(activeViewport.id);
      if (eng && eng.isPlaying) {
        eng.pause();
        // Sync engine's frame cursor to whatever cornerstone scrubs
        // to so a subsequent play() resumes from the scrolled
        // position rather than snapping back.
        Promise.resolve().then(() => {
          try {
            const idx = activeViewport.getCurrentImageIdIndex?.();
            if (typeof idx === "number" && eng.currentFrame !== idx) {
              eng.seekTo(idx);
            }
          } catch (err) {
            // ignore — engine might be disposed
          }
        });
      }
    };
    singleFrameWheelHandlers.set(activeViewport.id, wheelPauseHandler);
    try {
      activeViewport.element.addEventListener("wheel", wheelPauseHandler, {
        capture: true,
        passive: true,
      });
    } catch (err) {
      console.debug("[MultiFrame] failed to attach single-frame wheel handler:", err);
    }

    // Prefetch frames on both desktop and mobile so the first cine loop is
    // smooth and the buffer bar fills progressively; the engine awaits this
    // warmup promise on its first play(). This is safe on mobile: it warms
    // only the ONE active multi-frame instance's frames (not every instance
    // across the study — that path, prefetchRemainingFramesForStack, is
    // separately disabled) and goes through the shared throttle queue, which
    // caps concurrency so it can't saturate the browser's socket pool.
    const warmup = prefetchFrameImageIds(
      imageIdsForViewport,
      imageIndexVal,
      (idx) => engine.notifyFrameLoaded(idx),
      // Mobile: high-priority only for the forward window; the rest trickle in
      // at low priority (gradual buffer fill). Gated on sopUid so the sliding
      // window is wired to re-promote frames ahead of the cursor during play.
      isMobile && multiFrameContext.sopUid
        ? MOBILE_CINE_PREFETCH_WINDOW
        : Infinity
    );
    engine.setWarmupPromise(warmup);

    // AutoStart preference — if the user has opted in, kick off playback
    // immediately. play() internally waits on the warmup promise, so cine
    // doesn't actually tick until the buffer is ready.
    if (getMultiFrameAutoStart()) {
      engine.play();
    }
  } else if (mixedSeriesContext) {
    // Phase B — mixed-series two-axis cine. Stash orchestration state on
    // the viewport, attach a STACK_NEW_IMAGE listener so user scroll in
    // OUTER mode rebinds the engine to the new active instance, and spin
    // up the initial engine for the active instance (if multi-frame).
    const state = {
      outerStack: mixedSeriesContext.outerStack,
      instanceFrameStacks: mixedSeriesContext.instanceFrameStacks,
      currentOuterIdx: safeImageIndex,
      cineMode: "OUTER",
      viewport: activeViewport,
      viewportElement: activeViewport.element,
      stackNewImageHandler: null,
      wheelHandler: null,
      // Debounce timer for engine setup. Rapid stack scrolling across
      // several multi-frame instances would otherwise fire one
      // prefetchFrameImageIds per instance the user transits — N
      // instances × ~70 frames blew past the HTTP/1.1 socket pool and
      // caused ERR_INSUFFICIENT_RESOURCES. We hold off the prefetch
      // burst until the user actually settles on an instance.
      engineSetupTimer: null,
    };
    viewportMixedSeriesState.set(activeViewport.id, state);

    const handler = (event) => {
      const s = viewportMixedSeriesState.get(activeViewport.id);
      if (!s) return;
      // In INNER mode the viewport's stack is the current instance's
      // frames; STACK_NEW_IMAGE events are just the cine engine ticking
      // through frames. Ignore — the engine drives them.
      if (s.cineMode === "INNER") return;
      const newIdx =
        event?.detail?.imageIdIndex ??
        activeViewport.getCurrentImageIdIndex?.();
      if (typeof newIdx !== "number" || newIdx === s.currentOuterIdx) return;
      s.currentOuterIdx = newIdx;

      // Coalesce rapid scrolls — only fire engine setup (and its prefetch
      // burst) once the user pauses on an instance for ~300ms. Cancels any
      // pending setup for an instance the user has already scrolled past.
      if (s.engineSetupTimer != null) {
        clearTimeout(s.engineSetupTimer);
      }
      s.engineSetupTimer = setTimeout(() => {
        s.engineSetupTimer = null;
        // Re-check state: viewport could have been disposed during the
        // debounce window. Also re-read currentOuterIdx in case it
        // changed (shouldn't — the timer is cleared on each scroll —
        // but cheap defense).
        const stillState = viewportMixedSeriesState.get(activeViewport.id);
        if (!stillState) return;
        setupMixedSeriesEngineForInstance(activeViewport, false);
      }, 300);
    };
    state.stackNewImageHandler = handler;
    try {
      activeViewport.element.addEventListener(
        cornerstone.EVENTS.STACK_NEW_IMAGE,
        handler
      );
    } catch (err) {
      console.debug("[MultiFrame] failed to attach STACK_NEW_IMAGE handler:", err);
    }

    // Wheel handler — when the user scrolls during cine (INNER mode),
    // pause the engine and route the scroll to the OUTER instance
    // stack. Without this, the wheel scrubs frames of the current
    // instance because Cornerstone's stack scroll tool sees the INNER
    // stack (frames) and has no idea the viewport also belongs to an
    // outer instance stack. We intercept at capture phase so Cornerstone
    // never sees the event in INNER mode.
    const wheelHandler = (event) => {
      const s = viewportMixedSeriesState.get(activeViewport.id);
      if (!s) return;
      // OUTER mode — let Cornerstone handle the wheel normally
      // (scrolling between instances).
      if (s.cineMode !== "INNER") return;

      event.preventDefault();
      event.stopPropagation();

      // Pause without firing afterPause — we're about to setStack
      // ourselves to a (possibly different) outer index, so the
      // afterPause's setStack would just be a wasted call that fights
      // ours.
      const engine = getEngine(activeViewport.id);
      if (engine && engine.isPlaying) {
        engine.pause({ skipAfterPause: true });
      }

      const delta = event.deltaY > 0 ? +1 : -1;
      const nextOuterIdx = Math.max(
        0,
        Math.min(s.outerStack.length - 1, s.currentOuterIdx + delta)
      );

      // Flip back to OUTER mode and swap stack — even if the index
      // didn't change (already at the boundary), the user still
      // wants to be out of cine so the next wheel notch behaves
      // normally.
      s.cineMode = "OUTER";
      s.currentOuterIdx = nextOuterIdx;

      Promise.resolve()
        .then(() => activeViewport.setStack(s.outerStack, nextOuterIdx))
        .then(() => {
          activeViewport.render();
          // Re-spin up an engine for the new active instance (no
          // autoplay — scroll shouldn't start a cine).
          setupMixedSeriesEngineForInstance(activeViewport, false);
        })
        .catch((err) => {
          console.debug("[MultiFrame] wheel-driven outer scroll failed:", err);
        });
    };
    state.wheelHandler = wheelHandler;
    try {
      // Capture phase so we run BEFORE Cornerstone's StackScrollTool
      // wheel handler. `passive: false` so preventDefault works.
      activeViewport.element.addEventListener("wheel", wheelHandler, {
        capture: true,
        passive: false,
      });
    } catch (err) {
      console.debug("[MultiFrame] failed to attach wheel handler:", err);
    }

    // Initial engine for the active instance (if multi-frame). Autoplay
    // is allowed on this initial setup but skipped on subsequent
    // scroll-driven rebinds — surprising the user by auto-playing every
    // time they scroll into a multi-frame instance would be jarring.
    setupMixedSeriesEngineForInstance(activeViewport, true);

    // Sibling-instance prefetch intentionally disabled — eagerly
    // warming every multi-frame instance in the series at open time
    // burst-fired hundreds of XHRs to the same origin, exceeding the
    // browser's HTTP/1.1 socket pool and causing
    // net::ERR_INSUFFICIENT_RESOURCES. Sibling instances now warm on
    // demand when the user scrolls to them (handled by the
    // STACK_NEW_IMAGE handler / setupMixedSeriesEngineForInstance,
    // which calls prefetchFrameImageIds for the newly-active
    // instance).
  }

  // For a single-instance multiframe series the viewport stack contains N
  // per-frame imageIds (one per frame), but there is only ONE series image.
  // Return 0 so the caller sets imageCount=0, which pins the vertical
  // scrollbar at position 0 and prevents it from navigating frames.
  // Frame navigation is handled exclusively by the MultiFrameToolbar scrubber.
  return multiFrameContext ? 0 : imageIdsForViewport.length - 1;
};

// Tears down any existing engine on the viewport and creates a fresh one
// bound to the currently-active multi-frame instance (resolved via the
// viewport's mixed-series state). No-op if the active instance is single
// -frame — the user can still scroll through outer slots without an
// engine; only multi-frame slots get cine controls.
const setupMixedSeriesEngineForInstance = (viewport, allowAutoplay) => {
  disposeBusSubscription(viewport.id);
  disposeEngine(viewport.id);

  const state = viewportMixedSeriesState.get(viewport.id);
  if (!state) return;
  const frames = state.instanceFrameStacks.get(state.currentOuterIdx);
  if (!frames || frames.length < 2) return;

  const initialFps = resolveInitialFps(frames[0], null);
  const engine = new MultiFramePlaybackEngine({
    viewportId: viewport.id,
    totalFrames: frames.length,
    fps: initialFps,
    initialFrame: 0,
    onFrame: (frame) => {
      try {
        showMixedSeriesFrame(viewport, frame);
      } catch (err) {
        console.debug("[MultiFrame] mixed-series frame render failed:", err);
      }
    },
    beforePlay: () => orchestrateMixedSeriesPlay(viewport.id),
    // No afterPause: a paused clip stays on its frame (see showMixedSeriesFrame).
  });
  registerEngine(viewport.id, engine);
  busSubscriptions.set(viewport.id, wireEngineToBus(engine));
  // Mirror the real cache into the engine's loaded-set (see the single-instance
  // path) so the buffer bar reflects actual cached frames for this instance.
  startCacheSync(viewport.id, engine, frames);

  // Sliding-window prefetch over this instance's own frame array. The
  // single-instance path rebuilds frame URLs from UIDs (advancePrefetchWindow);
  // here we slide over the actual `frames` ids instead, which is correct for
  // every URL variant (UID-derived AND share-token) and needs no UID lookup.
  // This is what lets the bounded mobile warmup below stay small without the
  // cursor running into cold frames during cine (no continuous loader flashes).
  engine.setSlidingWindowCallback((frame0) => {
    const start = Math.max(0, frame0 - PREFETCH_LOOK_BEHIND);
    const end = Math.min(frames.length - 1, frame0 + PREFETCH_LOOK_AHEAD);
    for (let i = start; i <= end; i++) {
      const id = frames[i];
      if (!id) continue;
      const idx = i;
      if (isFrameCached(id)) {
        // Already in cache (e.g. warmed last loop) — record it so the engine's
        // loaded-set and the buffer bar stay accurate without re-fetching.
        engine.notifyFrameLoaded(idx);
        continue;
      }
      // Warm ahead, and tell the engine the moment each frame settles so
      // isCurrentFrameLoaded flips true before the cursor arrives — the
      // viewport stays smooth and the buffer bar fills progressively.
      enqueueFramePrefetch(id, { priority: "high" }).then(
        () => engine.notifyFrameLoaded(idx),
        () => engine.notifyFrameLoaded(idx)
      );
    }
  });

  // Prefetch on both desktop and mobile. On mobile the forward window is
  // high-priority and the rest trickles in at low priority (gradual buffer
  // fill); the sliding window above re-promotes frames ahead of the cursor to
  // high priority during play. Desktop warms the whole instance at high prio.
  const warmup = prefetchFrameImageIds(
    frames,
    0,
    (idx) => engine.notifyFrameLoaded(idx),
    isMobile ? MOBILE_CINE_PREFETCH_WINDOW : Infinity
  );
  engine.setWarmupPromise(warmup);

  if (allowAutoplay && getMultiFrameAutoStart()) {
    engine.play();
  }
};

export const updateViewport = async (
  viewportIndex,
  imageIndex,
  initialRange
) => {
  const activeViewport = await getViewport(viewportIndex);

  // Mixed-series cine: while a multi-frame instance is playing — or paused on
  // one of its frames — the live stack is the INNER frame array, so a plain
  // setImageIdIndex would land on a FRAME, not the instance the user picked
  // (and during play the engine's rAF tick would immediately override it).
  // This is why stack-scroll appears dead during cine on touch devices (no
  // wheel event to trigger the wheel-pause handler; the scrollbar is the only
  // navigation). Detect it, pause the engine, restore the OUTER (per-instance)
  // stack at the chosen index, and pin currentOuterIdx so the scrollbar /
  // overlays stay correct.
  const mixedState = viewportMixedSeriesState.get(activeViewport.id);
  if (mixedState && mixedState.cineMode === "INNER") {
    await showMixedSeriesInstance(activeViewport, imageIndex);
    return;
  }

  await activeViewport.setImageIdIndex(imageIndex);
  activeViewport.render();

};

export const changeStack = async (
  viewportIndex,
  thumbnailId,
  stack,
  originalStack,
  showOriginal,
  currentImageIndex,
  setLoaderRunning,
  ref
) => {
  // setLoaderRunning(true);
  // ref.current.continuousStart();
  const activeViewport = await getViewport(viewportIndex);
  if (!activeViewport) {
    // ref.current.complete();
    return;
  }
  // console.log(activeViewport);
  currentImageIndex = activeViewport.getCurrentImageIdIndex();

  const isDicomWebId = (id) => id && (id.startsWith("dicomweb:") || id.startsWith("wadouri:"));
  const targetIds = showOriginal ? originalStack?.imageIds : stack?.imageIds;
  // Block plain HTTP stacks (SR, PR) and any stack whose dicomweb URL points to a
  // non-image DICOM (PR originalStack URLs are dicomweb but the file has no pixel data)
  if (!targetIds?.length || !isDicomWebId(targetIds[0])) return;
  if (stack?.modality === "SR" || stack?.modality === "PR") return;

  if (showOriginal) {
    await activeViewport.setStack(originalStack.imageIds, currentImageIndex);
    // console.log(activeViewport)
    activeViewport.render();
    // ref.current.complete();
  } else {
    await activeViewport.setStack(stack.imageIds, currentImageIndex);
    activeViewport.render();
  }
  // ref.current.complete();
};

export const getCurrentImage = async (viewportIndex) => {
  const activeViewport = await getViewport(viewportIndex);
  return activeViewport.getCurrentImageIdIndex();
};

export const loadDefaultWindowLevelToolGroup = (
  activeViewport,
  activeViewportIndex
) => {
  // const toolGroup = getToolGroupManager(
  //   TOOL_GROUP_ID + "_" + activeViewportIndex
  // );
  const toolGroup = getToolGroupManager(TOOL_GROUP_ID);
  // toolGroup.addTool(cornerstonetools.WindowLevelTool.toolName);
  toolGroup.addViewport(activeViewport.id, RENDERING_ENGINE_ID);
  // toolGroup.setToolActive(cornerstonetools.WindowLevelTool.toolName, {
  //   bindings: [
  //     {
  //       mouseButton: MouseBindings.Primary, // Left Click
  //     },
  //   ],
  // });
  // console.log(toolGroup);
};

export const getInitialZoom = async (viewportIndex, setScale, event) => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const activeViewport = renderingEngine.getViewport(
    "viewport_" + viewportIndex
  );

  const imageData = activeViewport.getImageData();

  if (!imageData) return;

  if (event) {
    if (event.detail.camera.scale) {
      setScale(event.detail.camera.scale);
      return;
    }

    const { spacing } = imageData;

    const scale =
      (activeViewport.element.clientHeight * spacing[0] * 0.5) /
      event.detail.camera.parallelScale;

    setScale(scale);
    return;
  }

  if (activeViewport.initialCamera.scale) {
    setScale(activeViewport.initialCamera.scale);
    return;
  }

  const { spacing } = imageData;

  const scale =
    (activeViewport.element.clientHeight * spacing[0] * 0.5) /
    activeViewport.initialCamera.parallelScale;

  setScale(scale);
  return;
};

export const toggleReferenceLines = async (
  isReferenceLineEnabled,
  viewportIndex
) => {
  const activeViewport = await getViewport(viewportIndex);

  if (!activeViewport.getCurrentImageId()) {
    return;
  }
  const toolGroup = getToolGroupManager(TOOL_GROUP_ID);

  if (isReferenceLineEnabled) {
    // Determine the best source viewport for reference lines.
    // If the active viewport is single-image (e.g. topogram/scout), reference
    // lines won't render usefully on target viewports.  In that case, pick the
    // first multi-slice viewport as source so other viewports show correct lines.
    let sourceViewport = activeViewport;
    const activeImageIds = activeViewport.getImageIds?.() || [];
    if (activeImageIds.length <= 1) {
      try {
        const allViewports = await getAllViewports();
        const multiSliceViewport = allViewports.find(
          (vp) =>
            !vp.id.startsWith("thumbnail") &&
            vp.type === "stack" &&
            vp.getCurrentImageId() &&
            (vp.getImageIds?.() || []).length > 1
        );
        if (multiSliceViewport) {
          sourceViewport = multiSliceViewport;
        }
      } catch (e) {
        // Fallback to active viewport if lookup fails
      }
    }

    // Disable first so the tool picks up the new configuration cleanly.
    // Without this, re-enabling with a different sourceViewportId may be
    // silently ignored by Cornerstone if the tool is already enabled.
    toolGroup.setToolDisabled(cornerstonetools.ReferenceLinesTool.toolName);

    try {
      toolGroup.setToolConfiguration(
        cornerstonetools.ReferenceLinesTool.toolName,
        {
          sourceViewportId: sourceViewport.id,
        },
        true
      );
    } catch (error) {
      console.log(error);
    }
    toolGroup.setToolEnabled(cornerstonetools.ReferenceLinesTool.toolName);
  } else {
    toolGroup.setToolDisabled(cornerstonetools.ReferenceLinesTool.toolName);
  }

};

const deactivateAssignableCornerstoneTool = (toolGroup, name) => {
  try {
    toolGroup.setToolDisabled(name);
  } catch {
  }
};

export const setToolOnActiveViewport = async (
  viewportIndex,
  toolName,
  previousToolName,
  activeViewportIndex,
  mouseButtonToolAssignments
) => {
  const activeViewport = await getViewport(viewportIndex);

  if (!activeViewport.getCurrentImageId()) {
    return;
  }

  const toolGroup = getToolGroupManager(TOOL_GROUP_ID);
  // toolGroup.addViewport(activeViewport.id, RENDERING_ENGINE_ID);

  if (previousToolName) {
    if (PASSIVE_TOOLs.includes(previousToolName)) {
      toolGroup.setToolPassive(previousToolName);
    } else {
      toolGroup.setToolDisabled(previousToolName);
    }
  }

  // toolGroup.addTool(toolName);
  if (toolName === cornerstonetools.PlanarFreehandROITool.toolName) {
    if (viewportIndex === activeViewportIndex) {
      toolGroup.setToolConfiguration(
        cornerstonetools.PlanarFreehandROITool.toolName,
        {
          calculateStats: true,
        },
        false
      );
    }
  }

  if (toolName === cornerstonetools.ArrowAnnotateTool.toolName) {
    if (viewportIndex === activeViewportIndex) {
      toolGroup.setToolConfiguration(
        cornerstonetools.ArrowAnnotateTool.toolName,
        {
          getTextCallback: (doneChangingTextCallback) =>
            doneChangingTextCallback(prompt("Enter your annotation:")),
          changeTextCallback: (data, eventData, doneChangingTextCallback) =>
            doneChangingTextCallback(prompt("Change your annotation:")),
          // arrowFirst:true → arrowhead at points[0] (the first click / anchor); the
          // tail follows the cursor as you drag. Matches the Advance viewer (MedDream)
          // where the first click is the arrowhead. (This setToolConfiguration runs on
          // tool activation and is the effective live-drawing config, overriding the
          // addTool default in CornerstoneService.jsx.)
          arrowFirst: true,
        },
        false
      );
      const arrowName = cornerstonetools.ArrowAnnotateTool.toolName;
      //contains all the properties of Arrow Annotation
      //console.log('$$$$$$$$ Default tool styles:', annotation.config.style.getDefaultToolStyles());
  
      annotation.config.style.setToolGroupToolStyles(toolGroup.id, {
        [arrowName]: { 
        // Selected/active line color (the green)
          colorSelected: '#ffb632',       //  active color mustard
          colorHighlighted: '#2c9ec4',   //hover light green to blue
          lineWidth: 3, 
          color: '#ffb632',     //(when not selected)
          textBoxLineDash: [8, 4],
          textBoxLinkLineWidth: 2,   // leader thickness (makes each dot thicker)
      
          textBoxColor: '#ffb632',            // normal text + border + leader (when not selected)
          textBoxColorSelected: '#ffb632',      // when selected
          textBoxColorHighlighted: '#2c9ec4',   // on hover

          textBoxFontSize: '18px', 
    }});

      // Re-render so existing arrows update immediately
      const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
      _renderLoadedViewports(renderingEngine);

    }
  }

  const assignments =
    mouseButtonToolAssignments &&
    typeof mouseButtonToolAssignments === "object"
      ? mouseButtonToolAssignments
      : DEFAULT_MOUSE_BUTTON_TOOL_ASSIGNMENTS;

  for (const assignableName of MOUSE_ASSIGNABLE_TOOL_NAMES) {
    deactivateAssignableCornerstoneTool(toolGroup, assignableName);
  }

  const activeIsAssignable =
    MOUSE_ASSIGNABLE_TOOL_NAMES.includes(toolName);

  const slotDefs = [
    ["primary", MouseBindings.Primary],
    ["secondary", MouseBindings.Secondary],
    ["auxiliary", MouseBindings.Auxiliary],
  ];

  for (const [slot, mouseBinding] of slotDefs) {
    if (slot === "primary" && !activeIsAssignable) {
      continue;
    }
    const assignedTool = assignments[slot];
    if (
      !assignedTool ||
      !MOUSE_ASSIGNABLE_TOOL_NAMES.includes(assignedTool)
    ) {
      continue;
    }
    if (isMobile && activeIsAssignable && assignedTool !== toolName) {
      continue;
    }
    const useTouchZoom =
      assignedTool === cornerstonetools.ZoomTool.toolName &&
      isMobile &&
      slot === "primary";
    try {
      if (useTouchZoom) {
        toolGroup.setToolActive(assignedTool, {
          bindings: [{ numTouchPoints: 2 }],
        });
      } else if (isMobile && activeIsAssignable) {
        toolGroup.setToolActive(assignedTool, {
          bindings:
            assignedTool === cornerstonetools.ZoomTool.toolName
              ? [{ numTouchPoints: 2 }]
              : [{ numTouchPoints: 1 }],
        });
      } else {
        toolGroup.setToolActive(assignedTool, {
          bindings: [{ mouseButton: mouseBinding }],
        });
      }
    } catch (e) {
      console.warn("setToolActive failed for", assignedTool, e);
    }
  }

  if (isMobile && activeIsAssignable) {
    const inAssignments = [
      assignments.primary,
      assignments.secondary,
      assignments.auxiliary,
    ].includes(toolName);
    if (!inAssignments) {
      try {
        toolGroup.setToolActive(toolName, {
          bindings:
            toolName === cornerstonetools.ZoomTool.toolName
              ? [{ numTouchPoints: 2 }]
              : [{ numTouchPoints: 1 }],
        });
      } catch (e) {
        console.warn("setToolActive failed for", toolName, e);
      }
    }
  }

  const inUse = new Set(
    [assignments.primary, assignments.secondary, assignments.auxiliary].filter(
      (n) => n && MOUSE_ASSIGNABLE_TOOL_NAMES.includes(n)
    )
  );
  if (isMobile && activeIsAssignable) {
    inUse.add(toolName);
  }
  for (const n of MOUSE_ASSIGNABLE_TOOL_NAMES) {
    if (!inUse.has(n)) {
      try {
        toolGroup.setToolDisabled(n);
      } catch {
       
      }
    }
  }

  if (!activeIsAssignable) {
    try {
      if (toolName === cornerstonetools.ZoomTool.toolName && isMobile) {
        toolGroup.setToolActive(toolName, {
          bindings: [{ numTouchPoints: 2 }],
        });
      } else {
        toolGroup.setToolActive(toolName, {
          bindings: [{ mouseButton: MouseBindings.Primary }],
        });
      }
    } catch (e) {
      console.warn("setToolActive failed for active tool", toolName, e);
    }
  }
};

export const setInvert = async (viewportIndex) => {
  const activeViewport = await getViewport(viewportIndex);
  activeViewport.setProperties({
    invert: !activeViewport.getProperties().invert,
  });
  activeViewport.render();
};

export const setRotation = async (viewportIndex, rotationVal) => {
  const activeViewport = await getViewport(viewportIndex);
  let { rotation } = activeViewport.getViewPresentation();
  if(rotation === 0 && rotationVal < 0) rotation = 360; 
  activeViewport.setViewPresentation({
    rotation: rotation + rotationVal, 
  });

  activeViewport.render();
};

export const resetRotation = async (viewportIndex) => {
  const activeViewport = await getViewport(viewportIndex);

  activeViewport.setViewPresentation({
    rotation: 0,
  });

  activeViewport.render();
};

export const setRotationAbsolute = async (viewportIndex, angle) => {
  const activeViewport = await getViewport(viewportIndex);
  activeViewport.setViewPresentation({
    rotation: angle,
  });
  activeViewport.render();
};

export const getViewportRotation = async (viewportIndex) => {
  const activeViewport = await getViewport(viewportIndex);
  const { rotation } = activeViewport.getViewPresentation();
  return rotation || 0;
};

export const FlipVertically = async (viewportIndex) => {
  const activeViewport = await getViewport(viewportIndex);
  const { flipVertical } = activeViewport.getCamera();

  activeViewport.setCamera({
    flipVertical: !flipVertical,
  });
  activeViewport.render();
};

export const resetViewPlaneOfCamera = async (viewportIndex) => {
  const activeViewport = await getViewport(viewportIndex);
  activeViewport.setCamera({
    flipHorizontal: false,
    flipVertical: false,
    focalPoint: [0, 0, 0],  
    parallelProjection: true,
    parallelScale: 1, 
    position: [0, 0, -1], 
    rotation: 0,
    viewAngle: 90,
    viewPlaneNormal: [0, 0, -1],
    viewUp: [0, -1, 0],  
  });
  // setTimeout(() => {
    
  //   resetViewport();
  // }, 1000);
  
};

export const resetStackPosition = async (viewportIndex) => {
  const activeViewport = await getViewport(viewportIndex);
    let displayArea = createDisplayArea(1, 0.5);
    activeViewport.setOptions({
      displayArea: displayArea.displayArea,
      imageArea:displayArea.displayArea.imageArea,
      imageCanvasPoint:displayArea.displayArea.imageCanvasPoint,
    });

  activeViewport.render();
};

export const setStackPosition = async (viewportIndex, position, rotation, flipHorizontal) => {
  const activeViewport = await getViewport(viewportIndex);

  // Get current imageArea size from viewport
  const currentOptions = activeViewport.getOptions?.();
  const currentImageArea = currentOptions?.displayArea?.imageArea;
  const size = Array.isArray(currentImageArea) ? currentImageArea[0] : 1;

  // setOptions()/displayArea below recomputes the camera transform, which silently
  // resets any flipHorizontal/flipVertical already applied via FlipHorizontally/
  // FlipVertically — snapshot the live flip state so it can be restored afterward.
  const { flipHorizontal: currentFlipHorizontal, flipVertical: currentFlipVertical } =
    activeViewport.getCamera();

  let displayArea;
    switch (position) {
      case HP_VIEWPORT_ALIGNMENT.LEFT:
        displayArea = createDisplayArea(size, [0, 0.5], rotation, flipHorizontal); // Adjusted for left position
        break;
      case HP_VIEWPORT_ALIGNMENT.RIGHT:
        displayArea = createDisplayArea(size, [1, 0.5], rotation, flipHorizontal); // Adjusted for right position
        break;
      case HP_VIEWPORT_ALIGNMENT.CENTER:
      default:
        displayArea = createDisplayArea(size, [0.5, 0.5], rotation, flipHorizontal); // Centered display
        break;
    }
    if(displayArea){
      activeViewport.setOptions({
        displayArea: displayArea.displayArea,
        imageArea:displayArea.displayArea.imageArea,
        imageCanvasPoint:displayArea.displayArea.imageCanvasPoint,
      });
      if (rotation != null) {
        activeViewport.setViewPresentation({ rotation });
      }
      activeViewport.setCamera({
        flipHorizontal: currentFlipHorizontal,
        flipVertical: currentFlipVertical,
      });
    }

    activeViewport.render();
};

/**
 * Which way a left/right aligned image has to be nudged to stay flush with its
 * canvas edge, as a sign. Centre is 0: the canvas centre is the point the camera
 * scales about, so a centred image needs no nudge at all.
 *
 * Shared by the zoom re-pin below and by AlignmentLockService, which holds that
 * same edge against panning rather than against zooming.
 */
export const alignmentPanDirection = (alignment) =>
  alignment === HP_VIEWPORT_ALIGNMENT.LEFT
    ? 1
    : alignment === HP_VIEWPORT_ALIGNMENT.RIGHT
    ? -1
    : 0;

/**
 * Re-pin a left/right aligned image to the viewport edge after a zoom.
 *
 * `setStackPosition` aligns by pinning the image's own edge to the canvas edge,
 * but a change of parallelScale scales the picture about the canvas centre - so
 * a left-aligned image that was flush at x=0 slides off by half the canvas
 * times the zoom just applied. Centre alignment needs nothing: the anchor is
 * the point the scaling is about.
 */
const repinAlignmentAfterZoom = (activeViewport, alignment, zoomApplied) => {
  const direction = alignmentPanDirection(alignment);
  if (!direction || !Number.isFinite(zoomApplied) || zoomApplied === 1) return;

  const [panX, panY] = activeViewport.getPan();
  const halfWidth = activeViewport.element.clientWidth / 2;
  activeViewport.setPan([panX + direction * halfWidth * (zoomApplied - 1), panY]);
};

/**
 * Zoom a viewport to an absolute scale: `factor` canvas pixels per image pixel.
 *
 * This is the unit a hanging protocol's `customScaleValue` is written in, not a
 * multiple of the fit - the Advance Viewer's "original resolution" is literally
 * `setScale(1)`, so factor 1 here has to mean the same thing as the toolbar's
 * Original zoom. Hence the parallelScale is the one `originalResolution`
 * computes, divided by the factor.
 *
 * Cornerstone has its own `displayArea.type = "SCALE"` for this sum, but it
 * measures against the device-pixel canvas and switches interpolation to
 * NEAREST on the way through, which would make an HP zoom look unlike every
 * other zoom in the viewer.
 *
 * Call it after alignment and rotation - both go through the camera and would
 * otherwise recompute the fit and undo this.
 */
/**
 * The zoom a viewport is at right now, in the same absolute unit
 * `applyViewportScale` takes: canvas pixels per image pixel.
 *
 * The inverse of the parallelScale sum below, so a caller that is about to refit
 * the camera - which is what writing an alignment does - can put the zoom back
 * afterwards instead of dropping the radiologist (or the protocol) back to fit.
 */
export const getViewportScaleFactor = async (viewportIndex) => {
  const activeViewport = await getViewport(viewportIndex);
  const imageData = activeViewport?.getImageData();
  if (!imageData) return null;

  const { spacing } = imageData;
  const { parallelScale } = activeViewport.getCamera();
  if (!(parallelScale > 0)) return null;

  const factor =
    (activeViewport.element.clientHeight * spacing[0] * 0.5) / parallelScale;
  return Number.isFinite(factor) && factor > 0 ? factor : null;
};

export const applyViewportScale = async (viewportIndex, factor, alignment) => {
  if (!(factor > 0)) return;

  const activeViewport = await getViewport(viewportIndex);
  const imageData = activeViewport?.getImageData();
  if (!imageData) return;

  const { spacing } = imageData;
  const parallelScale =
    (activeViewport.element.clientHeight * spacing[0] * 0.5) / factor;
  if (!Number.isFinite(parallelScale) || parallelScale <= 0) return;

  const previousParallelScale = activeViewport.getCamera().parallelScale;
  activeViewport.setCamera({ parallelScale });
  repinAlignmentAfterZoom(activeViewport, alignment, previousParallelScale / parallelScale);

  activeViewport.render();
};

function createDisplayArea(
  size,
  pointValue,
  rotation = 0,
  flipHorizontal = false
) {
  const canvasValue = pointValue;
  const imagePoint = Array.isArray(pointValue)
    ? pointValue
    : [pointValue, pointValue];
  const canvasPoint = Array.isArray(canvasValue)
    ? canvasValue
    : [canvasValue, canvasValue];
  return {
    rotation,
    flipHorizontal,
    displayArea: {
      imageArea: Array.isArray(size) ? size : [size, size],
      imageCanvasPoint: {
        imagePoint,
        canvasPoint,
      },
    },
  };
}

export const FlipHorizontally = async (viewportIndex) => {
  const activeViewport = await getViewport(viewportIndex);
  const { flipHorizontal } = activeViewport.getCamera();
  
  activeViewport.setCamera({
    flipHorizontal: !flipHorizontal,
  });
  activeViewport.render();
};

export const applyViewportStates = async (viewportIndex, viewportState) => {
  const activeViewport = await getViewport(viewportIndex);
  if(viewportState?.camera)
    activeViewport.setCamera(viewportState?.camera);

  if(viewportState?.properties)
    activeViewport.setProperties(viewportState?.properties);
    

  activeViewport.render();
};

export const reset = async (
  viewportIndex,
  annotationData,
  annotationDataAI,
  addedAnnotation,
  isMPRActive = false ,
   maskImages,
  thumbnailIndex 
) => {
  let allAddedAnnotation = [];
  const annotationManager = annotation.state.getAnnotationManager();
  annotationManager.removeAllAnnotations();
  for (const key in annotationData) {
    for (const key2 in annotationData[key]) {
      annotationData[key][key2].map((item) => {
        if (item !== null || item !== undefined) {
          annotationManager.addAnnotation(item.annotation);
        }
      });
    }
  }
  
  for (const key in annotationDataAI) {
    for (const key2 in annotationDataAI[key]) {
      const exists = addedAnnotation.some(item => item.id == key2);
      if(exists) {
        annotationDataAI[key][key2].map((item) => {
          if (item !== null || item !== undefined) {
            annotationManager.addAnnotation(item.annotation);
          }
        });
      }
    }
  }

  let activeViewport = null;
  if (isMPRActive) {
    activeViewport = await getMPRViewport(viewportIndex);
  } else {
    activeViewport = await getViewport(viewportIndex);
  }

  if (maskImages) {
    const maskImageId = `${thumbnailIndex}_${activeViewport?.getCurrentImageIdIndex()}`;
    delete maskImages[maskImageId];
    for (const key in maskImages) {
      for (const key2 in maskImages[key].maskAnnotationData) {
        annotationManager.addAnnotation(maskImages[key].maskAnnotationData[key2]);
      }
    }
  }

  activeViewport.resetCamera({
    resetPan: true,
    resetZoom: true,
    resetToCenter: true,
    storeAsInitialCamera: true,
  });
  activeViewport.resetProperties();
  activeViewport.setProperties({
    invert: false,
  });

  await setStackPosition(viewportIndex, HP_VIEWPORT_ALIGNMENT.CENTER);
  activeViewport.render();
};

// Clears the rotation / flip / WW-WL / alignment a hanging protocol applied, so
// exiting the HP leaves the viewport in its natural state.
export const resetViewportTransforms = async (viewportIndex) => {
  try {
    const activeViewport = await getViewport(viewportIndex);
    if (!activeViewport) return;

    activeViewport.resetCamera({
      resetPan: true,
      resetZoom: true,
      resetToCenter: true,
      storeAsInitialCamera: true,
    });
    activeViewport.resetProperties();
    // setStackPosition deliberately preserves the live flip state, so clear it first.
    activeViewport.setCamera({ flipHorizontal: false, flipVertical: false });
    await setStackPosition(viewportIndex, HP_VIEWPORT_ALIGNMENT.CENTER, 0, false);
    activeViewport.render();
  } catch (error) {
    console.error(`Error clearing HP transforms on viewport ${viewportIndex}:`, error);
  }
};

export const resetViewport = async (viewportIndex) => {
  try {
    const activeViewport = await getViewport(viewportIndex);
    
    if (!activeViewport) {
      throw new Error(`Viewport not found for index: ${viewportIndex}`);
    }

    activeViewport.reset();
    activeViewport.resetCamera();
    activeViewport.render();
    
  } catch (error) {
    console.error(`Error resetting viewport at index ${viewportIndex}:`, error);
  }
};


export const fitToScreen = async (viewportIndex, isMPRActive) => {
  let activeViewport;
  if (isMPRActive) {
    activeViewport = await getMPRViewport(viewportIndex);
  } else {
    activeViewport = await getViewport(viewportIndex);
  }
  activeViewport.resetCamera();
  activeViewport.render();
};

export const originalResolution = async (viewportIndex, isMPRActive) => {
  let activeViewport;
  if (isMPRActive) {
    activeViewport = await getMPRViewport(viewportIndex);
  } else {
    activeViewport = await getViewport(viewportIndex);
  }
  activeViewport.resetCamera();
  const camera = activeViewport.getCamera();
  const { focalPoint, position } = camera;
  const imageData = activeViewport.getImageData();
  const { spacing } = imageData;
  const parallelScale = activeViewport.element.clientHeight * spacing[0] * 0.5;
  activeViewport.setCamera({
    parallelScale,
    focalPoint,
    position,
  });
  activeViewport.render();
};

// Returns the THUMBNAIL INDEX of the current study's first sagittal ("lateral")
// series, or -1. `seriesCount` bounds the search to the current study.
//
// Two traps this guards against, both of which only bite in the priors viewer:
//   - The registry holds EVERY study's series, and each prior's first series is
//     mounted eagerly — so it also contains priors, and in load order, not index
//     order (e.g. 0, 14, 16, 30, 1, 2, 3...). Matching an unscoped list let a
//     PRIOR study's geometry decide what got hung next to the current study.
//   - Reading the thumbnail number off the array position rather than the
//     registry key then hung a completely unrelated series (position 2 -> the
//     current study's 3rd series, because thumbnail 16 sat at position 2).
// Sorting by index also drops the stray key -1 the registry can pick up.
export const checkForLateralSeries = async (seriesCount = Infinity) => {
  const candidates = (await getAllThumbnailViewports())
    // index 0 is already hung in the first viewport; < seriesCount keeps priors out
    .filter((viewport) => viewport.index > 0 && viewport.index < seriesCount)
    .sort((a, b) => a.index - b.index);

  for (const candidate of candidates) {
    const imageId = await loadAndCacheImage(candidate.imageIds[0]);
    const imagePlane = cornerstone.metaData.get(
      "imagePlaneModule",
      imageId.imageId
    );
    if (imagePlane && imagePlane.rowCosines && imagePlane.columnCosines) {
      const row = [
        Math.round(imagePlane.rowCosines[0]),
        Math.round(imagePlane.rowCosines[1]),
        Math.round(imagePlane.rowCosines[2])
      ];
      const column = [
        Math.round(imagePlane.columnCosines[0]),
        Math.round(imagePlane.columnCosines[1]),
        Math.round(imagePlane.columnCosines[2])
      ];
      const normal = [];
      vec3.cross(normal, column, row);

      if (Math.abs(normal[0]) === 1)
        //Sagittal{
        return candidate.index;
    }

  }
  return -1;
};

export const showOutlineOnViewport = async (
  viewportIndex,
  thumbnailIndex,
  imageId
) => {
  const viewport = await getViewport(viewportIndex);
  const element = viewport.element;
  const canvas = element.children[0].children[0];
  const ctx = canvas.getContext("2d");
  var img = new Image();
  img.src = imageId;

  ctx.drawImage(img, 0, 0);
  viewport.render();
};

export const loadDownloadImageViewport = async (
  activeViewportIndex,
  viewportIndex
) => {
  const activeViewport = await getViewport(activeViewportIndex);
  if (!activeViewport) return;
  const downloadImageViewport = await getViewport(viewportIndex);
  await downloadImageViewport.setStack(activeViewport.imageIds, 0);
  downloadImageViewport.render();
  return activeViewport.imageIds.length - 1;
};

export const downloadImageFromCanvas = async (
  stackId,
  currentImage,
  viewportIndex,
  filename
) => {
  const activeViewport = await getViewport(viewportIndex);
  const ele = activeViewport.element;
  setTimeout(() => {
    let data = ele
      .querySelector(".viewport-element")
      .querySelector("canvas")
      .toDataURL("image/" + "jpeg", 1);
    const link = document.createElement("a");
    link.href = data;
    if (filename === "") {
      link.setAttribute("download", `${stackId}_${currentImage + 1}.jpeg`);
    } else {
      link.setAttribute("download", `${filename}.jpeg`);
    }
    link.click();
    return;
  }, 1000);
};

export const downloadSeriesFromCanvas = async (
  stackId,
  stack,
  imageCount,
  setLoadingState,
  setScrollCount,
  filename
) => {
  const activeViewport = await getViewport(VIEWPORT_ROW * VIEWPORT_COLUMN + 1);

  if (
    activeViewport.getCurrentImageIdIndex() + 1 ===
    activeViewport.imageIds.length
  ) {
    await downloadImageFromCanvas(
      stackId,
      activeViewport.getCurrentImageIdIndex(),
      VIEWPORT_ROW * VIEWPORT_COLUMN + 1,
      filename
    );
    setLoadingState(false);
    return;
  }

  const ele = activeViewport.element;
  setTimeout(async () => {
    let data = ele
      .querySelector(".viewport-element")
      .querySelector("canvas")
      .toDataURL("image/" + "jpeg", 1);
    const link = document.createElement("a");
    link.href = data;
    if (filename === "") {
      link.setAttribute(
        "download",
        `${stackId}_${activeViewport.getCurrentImageIdIndex() + 1}.jpeg`
      );
    } else {
      link.setAttribute(
        "download",
        `${filename}_${activeViewport.getCurrentImageIdIndex() + 1}.jpeg`
      );
    }
    link.click();
    await activeViewport.setImageIdIndex(
      activeViewport.getCurrentImageIdIndex() + 1
    );
    setScrollCount(activeViewport.getCurrentImageIdIndex());
    downloadSeriesFromCanvas(
      stackId,
      stack,
      imageCount,
      setLoadingState,
      setScrollCount,
      filename
    );
  }, 1000);
};

const MPR_ELEMENT_WAIT_TIMEOUT_MS = 1500;
const MPR_ELEMENT_POLL_INTERVAL_MS = 20;

// The MPR panes only exist in the DOM once React has committed the render
// triggered by setIsMPRActive(true) — callers used to cover that render gap
// with a flat 1500ms setTimeout before calling loadMPR (and therefore this
// function) at all. Polling resolves as soon as all three elements exist,
// which normally happens within a frame or two, instead of always waiting
// out the full 1500ms; the same 1500ms is kept as a cap so an unusually
// slow render still falls back to exactly the old worst-case behavior.
const waitForMPRElements = () => {
  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      const axialElement = $("#viewport_axial").find("#viewportEntry")[0];
      const sagittalElement = $("#viewport_sagittal").find("#viewportEntry")[0];
      const coronalElement = $("#viewport_coronal").find("#viewportEntry")[0];
      if (
        (axialElement && sagittalElement && coronalElement) ||
        Date.now() - start >= MPR_ELEMENT_WAIT_TIMEOUT_MS
      ) {
        resolve({ axialElement, sagittalElement, coronalElement });
        return;
      }
      setTimeout(check, MPR_ELEMENT_POLL_INTERVAL_MS);
    };
    check();
  });
};

export const activateMPRViewports = async (
  setMprViewportElements,
  setIsMPRViewportActivated
) => {
  // ✓ Volume loaders now registered in initialize() to prevent race conditions
  const { axialElement, sagittalElement, coronalElement } = await waitForMPRElements();

  const mprViewportInputs = getMPRViewportInputs(
    axialElement,
    sagittalElement,
    coronalElement
  );
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);

  mprViewportInputs.forEach((viewport) => {
    renderingEngine.enableElement(viewport);
    renderingEngine
      .getViewport(viewport.viewportId)
      .element.addEventListener(
        cs3DToolsEvents.MOUSE_CLICK,
        contextMenuHandleClick
      );
  });

  const toolGroup = getToolGroupManager(MPR_TOOL_GROUP_ID);
  for (let i = 0; i < 3; i++) {
    toolGroup.addViewport(MPRViewportsId[i], RENDERING_ENGINE_ID);
  }

  // Pre-register every tool that can be used in MPR so mode switches never
  // fail with "not added to toolGroup". Tools already registered are skipped.
  const mprToolNames = [
    cornerstonetools.StackScrollTool.toolName,
    cornerstonetools.ZoomTool.toolName,
    cornerstonetools.WindowLevelTool.toolName,
    cornerstonetools.PanTool.toolName,
    cornerstonetools.ReferenceLinesTool.toolName,
    cornerstonetools.LengthTool.toolName,
    cornerstonetools.BidirectionalTool.toolName,
    cornerstonetools.EllipticalROITool.toolName,
    cornerstonetools.RectangleROITool.toolName,
    cornerstonetools.ProbeTool.toolName,
    cornerstonetools.ArrowAnnotateTool.toolName,
    cornerstonetools.AngleTool.toolName,
    cornerstonetools.CobbAngleTool.toolName,
    cornerstonetools.PlanarRotateTool.toolName,
  ];
  mprToolNames.forEach((name) => {
    if (!toolGroup._toolInstances?.[name]) {
      toolGroup.addTool(name);
    }
  });

  // Flexpoly (PlanarFreehandROI) is registered separately from the loop above
  // because it needs cachedStats to report the drawn ROI's area — same config
  // the 2D stack tool group uses (see addToolToToolGroupManager).
  if (!toolGroup._toolInstances?.[cornerstonetools.PlanarFreehandROITool.toolName]) {
    toolGroup.addTool(cornerstonetools.PlanarFreehandROITool.toolName, {
      cachedStats: true,
    });
  }

  // Mouse-wheel slice scrolling: StackScrollTool only responds to mouse-drag
  // (mouseDragCallback) unless it also has a Wheel binding, which the "Stack
  // Scroll" MPR tool never sets up. SeriesStackScrollMouseWheelTool implements
  // mouseWheelCallback and works on VolumeViewport (axial/sagittal/coronal), so
  // register it always-active on the wheel, independent of the selected tool —
  // matching the always-on Zoom/Pan bindings below.
  if (!toolGroup._toolInstances?.[SeriesStackScrollMouseWheelTool.toolName]) {
    toolGroup.addTool(SeriesStackScrollMouseWheelTool.toolName);
  }
  toolGroup.setToolActive(SeriesStackScrollMouseWheelTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Wheel }],
  });

  // CrosshairsTool needs explicit viewport callbacks so it knows which viewports
  // participate in synchronization. Without these, it computes invalid world→image
  // mappings that produce "No imageId found within the specified criteria" warnings.
  if (!toolGroup._toolInstances?.[cornerstonetools.CrosshairsTool.toolName]) {
    toolGroup.addTool(cornerstonetools.CrosshairsTool.toolName, {
      getReferenceLineControllable: (viewportId) =>
        MPRViewportsId.includes(viewportId),
      getReferenceLineDraggableRotatable: (viewportId) =>
        MPRViewportsId.includes(viewportId),
      getReferenceLineSlabThicknessControlsOn: (viewportId) =>
        MPRViewportsId.includes(viewportId),
    });
  }

  // Annotation tools should be passive so they render annotations
  const passiveTools = [
    cornerstonetools.LengthTool.toolName,
    cornerstonetools.BidirectionalTool.toolName,
    cornerstonetools.EllipticalROITool.toolName,
    cornerstonetools.RectangleROITool.toolName,
    cornerstonetools.ProbeTool.toolName,
    cornerstonetools.ArrowAnnotateTool.toolName,
    cornerstonetools.AngleTool.toolName,
    cornerstonetools.CobbAngleTool.toolName,
    cornerstonetools.PlanarFreehandROITool.toolName,
  ];
  passiveTools.forEach((name) => toolGroup.setToolPassive(name));

  // Zoom and Pan always available on secondary/auxiliary mouse buttons
  toolGroup.setToolActive(cornerstonetools.ZoomTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Secondary }],
  });
  toolGroup.setToolActive(cornerstonetools.PanTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Auxiliary }],
  });

  setMprViewportElements({
    axial: renderingEngine.getViewport(MPRViewportsId[0]).element,
    sagittal: renderingEngine.getViewport(MPRViewportsId[1]).element,
    coronal: renderingEngine.getViewport(MPRViewportsId[2]).element,
  });
  setIsMPRViewportActivated(true);
};

export const activate3DViewport = async () => {
  const element3D = $("#viewport_3d").find("#viewportEntry")[0];
  if (!element3D) return;

  const viewportInput = get3DViewportInput(element3D);
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);

  renderingEngine.enableElement(viewportInput);
  renderingEngine
    .getViewport(VOLUME_3D_VIEWPORT_ID)
    .element.addEventListener(
      cs3DToolsEvents.MOUSE_CLICK,
      contextMenuHandleClick
    );

  // Create a dedicated tool group for the 3D viewport with TrackballRotate as default.
  // Note: CrosshairsTool (and ReferenceLines/StackScroll/annotation tools) have no
  // support for ViewportType.VOLUME_3D (perspective volume rendering) in cornerstone3d,
  // so TrackballRotate remains the fallback whenever the MPR toolbar picks one of those.
  const toolGroup = getToolGroupManager(VOLUME_3D_TOOL_GROUP_ID);
  toolGroup.addViewport(VOLUME_3D_VIEWPORT_ID, RENDERING_ENGINE_ID);

  if (!toolGroup._toolInstances?.[cornerstonetools.TrackballRotateTool.toolName]) {
    toolGroup.addTool(cornerstonetools.TrackballRotateTool.toolName);
  }
  toolGroup.setToolActive(cornerstonetools.TrackballRotateTool.toolName, {
    bindings: [
      { mouseButton: MouseBindings.Primary },
    ],
  });

  // Zoom/Pan/WindowLevel are camera- and VOI-based, so they work fine on a
  // VOLUME_3D viewport. Pre-register them and keep Zoom/Pan always available
  // on secondary/auxiliary, mirroring the MPR tool group convention.
  const volume3DToolNames = [
    cornerstonetools.ZoomTool.toolName,
    cornerstonetools.PanTool.toolName,
    cornerstonetools.WindowLevelTool.toolName,
  ];
  volume3DToolNames.forEach((name) => {
    if (!toolGroup._toolInstances?.[name]) {
      toolGroup.addTool(name);
    }
  });
  toolGroup.setToolActive(cornerstonetools.ZoomTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Secondary }],
  });
  toolGroup.setToolActive(cornerstonetools.PanTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Auxiliary }],
  });
};

// Tools that are camera/VOI-based and therefore actually work on the VOLUME_3D
// viewport. Everything else the MPR toolbar can select (Crosshairs, ReferenceLines,
// StackScroll, and the annotation tools) needs 2D slice/cross-viewport geometry that
// VOLUME_3D doesn't have, so TrackballRotate is the fallback for those.
export const MIRRORABLE_3D_TOOL_NAMES = [
  cornerstonetools.ZoomTool.toolName,
  cornerstonetools.PanTool.toolName,
  cornerstonetools.WindowLevelTool.toolName,
];

// The MPR toolbar has one shared "active tool" (activeMPRTool) for all four
// panes, but the 3D pane can't run most of them. This returns which tool
// should be shown highlighted for a given active pane: the real activeMPRTool
// everywhere except the 3D pane, where it falls back to displaying the rotate
// tool as active whenever the real tool isn't one of the mirrorable ones.
// Mirrors the runtime fallback in syncPrimaryToolTo3DViewport without ever
// mutating activeMPRTool itself, so switching back to an MPR pane still shows
// whatever tool was actually selected.
export const getDisplayedMPRTool = (activeMPRTool, activeMPRViewportIndex) => {
  if (
    activeMPRViewportIndex === VOLUME_3D_VIEWPORT_INDEX &&
    !MIRRORABLE_3D_TOOL_NAMES.includes(activeMPRTool)
  ) {
    return cornerstonetools.PlanarRotateTool.toolName;
  }
  return activeMPRTool;
};

// Mirrors the MPR toolbar's active tool onto the 3D volume-render viewport's
// primary mouse button, disabling TrackballRotate while it's mirrored. No-op if
// the 3D viewport isn't currently enabled.
export const syncPrimaryToolTo3DViewport = async (toolName, oldToolName) => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const viewport3D = renderingEngine.getViewport(VOLUME_3D_VIEWPORT_ID);
  if (!viewport3D) return;

  const toolGroup = getToolGroupManager(VOLUME_3D_TOOL_GROUP_ID);
  const { ZoomTool, PanTool, WindowLevelTool, TrackballRotateTool } =
    cornerstonetools;

  if (
    oldToolName &&
    oldToolName !== toolName &&
    toolGroup._toolInstances?.[oldToolName]
  ) {
    toolGroup.setToolDisabled(oldToolName);
    if (oldToolName === ZoomTool.toolName) {
      toolGroup.setToolActive(ZoomTool.toolName, {
        bindings: [{ mouseButton: MouseBindings.Secondary }],
      });
    } else if (oldToolName === PanTool.toolName) {
      toolGroup.setToolActive(PanTool.toolName, {
        bindings: [{ mouseButton: MouseBindings.Auxiliary }],
      });
    }
  }

  if (!MIRRORABLE_3D_TOOL_NAMES.includes(toolName)) {
    toolGroup.setToolActive(TrackballRotateTool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Primary }],
    });
    return;
  }

  toolGroup.setToolDisabled(TrackballRotateTool.toolName);
  if (!toolGroup._toolInstances?.[toolName]) {
    toolGroup.addTool(toolName);
  }
  toolGroup.setToolActive(toolName, {
    bindings: [{ mouseButton: MouseBindings.Primary }],
  });
};

let _initial3DCamera = null;
// The preset (and the VOI/transfer function it encodes) applied when the 3D
// viewport was loaded, so Reset can restore it — otherwise a WindowLevelTool
// drag on the 3D pane survives Reset since resetCamera never touches VOI.
let _initial3DPreset = null;

export const load3DViewport = async (volumeId) => {
  _initial3DCamera = null;
  _initial3DPreset = null;
  await activate3DViewport();

  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const viewport3D = renderingEngine.getViewport(VOLUME_3D_VIEWPORT_ID);
  if (!viewport3D) return;

  await viewport3D.setVolumes([{ volumeId }]);

  // Apply a default CT volume rendering preset
  const presets = cornerstone.CONSTANTS.VIEWPORT_PRESETS;
  const defaultPreset = presets.find((p) => p.name === "CT-Bone") || presets[0];
  if (defaultPreset) {
    viewport3D.setProperties({ preset: defaultPreset });
    _initial3DPreset = defaultPreset;
  }

  viewport3D.render();

  // Snapshot the camera after the volume is fully positioned so reset can
  // restore it exactly, regardless of what TrackballRotateTool does later.
  const cam = viewport3D.getCamera();
  _initial3DCamera = {
    position: [...cam.position],
    focalPoint: [...cam.focalPoint],
    viewUp: [...cam.viewUp],
    parallelScale: cam.parallelScale,
    viewAngle: cam.viewAngle,
  };
};

export const disable3DViewport = async () => {
  _initial3DCamera = null;
  _initial3DPreset = null;
  try {
    // Remove from dedicated 3D tool group
    const toolGroup = getToolGroupManager(VOLUME_3D_TOOL_GROUP_ID);
    try {
      toolGroup.removeViewports(RENDERING_ENGINE_ID, VOLUME_3D_VIEWPORT_ID);
    } catch (_) { /* viewport may not be in group */ }

    const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
    const viewport3D = renderingEngine.getViewport(VOLUME_3D_VIEWPORT_ID);
    if (viewport3D) {
      renderingEngine.disableElement(VOLUME_3D_VIEWPORT_ID);
    }
  } catch (e) {
    console.warn("Error disabling 3D viewport:", e);
  }
};

// Disable the three MPR orthographic viewports so the rendering engine no
// longer holds stale references to their (soon-to-be-detached) DOM elements.
// Without this, after MPR exit the engine renders to the detached canvases
// during the next performResize/render cycle, corrupting the WebGL pipeline
// and causing viewport_1 to display viewport_0's content.
export const disableMPRViewports = async () => {
  try {
    const toolGroup = getToolGroupManager(MPR_TOOL_GROUP_ID);
    try {
      MPRViewportsId.forEach((id) => {
        toolGroup.removeViewports(RENDERING_ENGINE_ID, id);
      });
    } catch (_) { /* viewports may not be in group */ }

    const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
    MPRViewportsId.forEach((id) => {
      try {
        if (renderingEngine.getViewport(id)) {
          renderingEngine.disableElement(id);
        }
      } catch (_) { /* already removed or never created */ }
    });
  } catch (e) {
    console.warn("Error disabling MPR viewports:", e);
  }
};

// Releases the MPR/3D viewports' GPU resources and clears the volume cache.
// Must be called whenever MPR is torn down for ANY reason — an explicit
// "Exit MPR" click, or an INCIDENTAL exit (switching series/study while MPR
// happened to be active) — not just the toolbar's Exit button. This app
// renders through a single shared WebGL context (webGlContextCount: 1 in
// CornerstoneService.jsx); a `setIsMPRActive(false)` on its own only hides
// the panes in React — the previous volume's GPU texture and the 3 enabled
// MPR viewports stay fully resident. The next MPR activation then has to
// coexist with that orphaned volume, doubling GPU memory pressure and
// reliably tipping into CONTEXT_LOST_WEBGL when building MPR for multiple
// series one after another in the same study.
export const exitMPRViewports = async (is3DActive, setIs3DActive, setIsMPRActive) => {
  // Leaving MPR drops the tool group state, so the next MPR session starts with
  // crosshairs on again (loadMPR selects Crosshairs as its default tool).
  mprCrosshairsHidden = false;
  if (is3DActive) {
    setIs3DActive?.(false);
    await disable3DViewport();
  }
  await disableMPRViewports();
  setIsMPRActive?.(false);
  cornerstone.cache.purgeVolumeCache();
  await resizeViewport();
};

export const reset3DViewport = async () => {
  try {
    const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
    const viewport3D = renderingEngine.getViewport(VOLUME_3D_VIEWPORT_ID);
    if (!viewport3D) return;

    // Restore the default preset first — this resets the VOI/transfer
    // function a WindowLevelTool drag would have modified. Falls back to
    // cornerstone's own resetProperties if we never captured one.
    if (_initial3DPreset) {
      viewport3D.setProperties({ preset: _initial3DPreset });
    } else {
      viewport3D.resetProperties();
    }

    if (_initial3DCamera) {
      viewport3D.setCamera({
        position: [..._initial3DCamera.position],
        focalPoint: [..._initial3DCamera.focalPoint],
        viewUp: [..._initial3DCamera.viewUp],
        parallelScale: _initial3DCamera.parallelScale,
        viewAngle: _initial3DCamera.viewAngle,
      });
    } else {
      viewport3D.resetCamera();
    }

    viewport3D.render();
  } catch (e) {
    console.warn("Error resetting 3D viewport:", e);
  }
};

// Resets every MPR viewport — the three orthographic panes (pan/zoom/rotation)
// plus the 3D volume-render pane when it's active — not just whichever pane
// the Reset button happened to be scoped to before.
export const resetMPRViewports = async () => {
  try {
    const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
    MPRViewportsId.forEach((viewportId) => {
      const viewport = renderingEngine.getViewport(viewportId);
      if (!viewport) return;
      // Drop any measurements (e.g. Length) drawn on this pane. Annotations are
      // grouped by FrameOfReferenceUID, which all three MPR panes share since
      // they render the same volume, so this also clears the other panes'
      // view of the same annotations.
      annotation.state.removeAnnotations(undefined, viewport.element);
      // resetCamera's default resetRotation:true already restores the correct
      // axis-aligned viewUp/viewPlaneNormal for this orientation — undoing any
      // PlanarRotateTool drag. Also calling setViewPresentation({rotation:0})
      // re-triggers BaseVolumeViewport's own setRotation pan/viewUp juggling on
      // top of that, which left the camera in a state where further Rotate
      // drags stopped working.
      // resetToCenter:false keeps the focal point's position along the slice
      // axis (the currently displayed image) while resetPan:true still
      // recenters it in-plane — resetToCenter:true would jump to the volume's
      // middle slice, changing which image is on screen.
      viewport.resetCamera({
        resetPan: true,
        resetZoom: true,
        resetToCenter: false,
      });
      // Restore the volume's default window/level too — otherwise a
      // WindowLevelTool drag survives the Reset button untouched.
      viewport.resetProperties();
      viewport.render();
    });
  } catch (e) {
    console.warn("Error resetting MPR viewports:", e);
  }

  await reset3DViewport();
};

export const loadMPR = async (
  viewportIndex,
  viewportType,
  seriesId,
  setMprLoading,
  setMprViewportElements,
  setIsMPRViewportActivated,
  setVolumeId,
  excludeImageIds = [],
  previousVolumeId
) => {
  let viewport;
  if (viewportType === "viewport") {
    viewport = await getViewport(viewportIndex);
  } else {
    viewport = await getThumbnailViewport(viewportIndex);
  }

  // Instances isReconstructable flagged as a different geometry (e.g. a
  // scout/localizer bundled into the series) are excluded from the volume —
  // including them would stack a mismatched-orientation slice into a
  // rectangular volume that assumes uniform per-slice geometry.
  const imageIds = excludeImageIds.length
    ? viewport.imageIds.filter((id) => !excludeImageIds.includes(id))
    : viewport.imageIds;

  // A series whose full-resolution volume would exceed MAX_MPR_VOLUME_BYTES
  // gets k-axis decimated (every Nth slice dropped, in-plane resolution kept
  // intact) via cornerstone3D's built-in decimatedVolumeLoader instead of
  // loading at full resolution and risking the WebGL context-loss crash this
  // guarded against before (see the "decimatedStreamingVolume" registration
  // in CornerstoneService.jsx).
  let kDecimation = 1;
  const firstImagePixel = cornerstone.metaData.get(
    "imagePixelModule",
    imageIds[0]
  );
  const rows = Number(firstImagePixel?.rows);
  const columns = Number(firstImagePixel?.columns);
  if (rows && columns) {
    const fullResolutionBytes = rows * columns * imageIds.length * 4;
    if (fullResolutionBytes > MAX_MPR_VOLUME_BYTES) {
      kDecimation = Math.ceil(fullResolutionBytes / MAX_MPR_VOLUME_BYTES);
    }
  }
  const volumeScheme =
    kDecimation > 1 ? "decimatedStreamingVolume" : "cornerstoneStreamingImageVolume";
  const volumeId = `${volumeScheme}: myVolume_${seriesId}`;
  setVolumeId(volumeId);
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  await activateMPRViewports(setMprViewportElements, setIsMPRViewportActivated);

  try {
    // Reuse volume if already cached, otherwise create it. Any previous
    // volume is still bound to the live MPR viewports at this point, so it
    // must not be freed yet — purging it here would delete the imageData
    // those viewports are actively rendering out from under them, and the
    // setVolumes() call below would throw (kicking the user out of MPR
    // mode). It's freed only after the viewports have switched over, below.
    let volume = cornerstone.cache.getVolume(volumeId);
    if (!volume) {
      makeBytesAvailableInCache();
      volume = await createAndCacheVolume(
        volumeId,
        kDecimation > 1
          ? { imageIds, ijkDecimation: [1, 1, kDecimation] }
          : { imageIds }
      );
      volume.load();
    }

    for (let i = 0; i < MPRViewportsId.length; i++) {
      const vp = renderingEngine.getViewport(MPRViewportsId[i]);
      await vp.setVolumes([{ volumeId }]);
      // setVolumes' internal resetCamera already lands on the volume's middle
      // slice, so the scrollbar starts centered.
      vp.render();
    }

    renderMPRViewport();

    // Every MPR viewport has now moved on to the new volume — the previous
    // one (a different series) is safe to free.
    if (
      previousVolumeId &&
      previousVolumeId !== volumeId &&
      cornerstone.cache.getVolume(previousVolumeId)
    ) {
      try {
        cornerstone.cache.removeVolumeLoadObject(previousVolumeId);
      } catch (_) { /* best-effort cleanup */ }
    }
  } catch (error) {
    // A pathologically large volume — or one stacked on top of a still-
    // resident previous MPR volume — can blow the single shared WebGL
    // context this app renders through (webGlContextCount: 1). Run the same
    // context-loss backstop the 2D stack path uses so the user gets the
    // guided-reload prompt instead of a silently dead viewer.
    checkEngineContextLost(renderingEngine);
    // activateMPRViewports() above already enabled the 3 MPR viewports before
    // this failure — release them now instead of leaving them orphaned (with
    // no volume) for the next MPR attempt to stack on top of.
    try {
      await disableMPRViewports();
    } catch (_) { /* best-effort cleanup, don't mask the original error */ }
    throw error;
  }
};

export const renderMPRViewport = async () => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const viewports = renderingEngine.getVolumeViewports();
  viewports.forEach((viewport) => {
    viewport.render();
  });
};

export const getInitialZoomForMPR = async (viewportIndex, setScale, event) => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const activeViewport = renderingEngine.getViewport(
    MPRViewportsId[viewportIndex]
  );
  let imageData;
  try {
    imageData = activeViewport.getImageData();
  } catch (error) {
    console.warn(error);
  }
  if (!imageData) return;

  if (event) {
    if (event.detail.camera.scale) {
      setScale(event.detail.camera.scale);
      return;
    }

    const { spacing } = imageData;

    const scale =
      (activeViewport.element.clientHeight * spacing[0] * 0.5) /
      event.detail.camera.parallelScale;

    setScale(scale);
    return;
  }

  if (activeViewport.initialCamera.scale) {
    setScale(activeViewport.initialCamera.scale);
    return;
  }

  const { spacing } = imageData;

  const scale =
    (activeViewport.element.clientHeight * spacing[0] * 0.5) /
    activeViewport.initialCamera.parallelScale;

  setScale(scale);
  return;
};

export const getInitialZoomFor3D = async (setScale, event) => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const activeViewport = renderingEngine.getViewport(VOLUME_3D_VIEWPORT_ID);
  if (!activeViewport) return;

  let imageData;
  try {
    imageData = activeViewport.getImageData();
  } catch (error) {
    console.warn(error);
  }
  if (!imageData) return;

  const { spacing } = imageData;

  if (event) {
    if (event.detail.camera.scale) {
      setScale(event.detail.camera.scale);
      return;
    }

    const scale =
      (activeViewport.element.clientHeight * spacing[0] * 0.5) /
      event.detail.camera.parallelScale;

    setScale(scale);
    return;
  }

  if (activeViewport.initialCamera?.scale) {
    setScale(activeViewport.initialCamera.scale);
    return;
  }

  const scale =
    (activeViewport.element.clientHeight * spacing[0] * 0.5) /
    activeViewport.initialCamera.parallelScale;

  setScale(scale);
};

export const getSliceData = async (
  viewportIndex,
  setImageCount,
  setCurrentImage
) => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const viewport = renderingEngine.getViewport(MPRViewportsId[viewportIndex]);
  const sliceData = getImageSliceDataForVolumeViewport(viewport);

  setImageCount(sliceData.numberOfSlices);
  setCurrentImage(sliceData.imageIndex);
};

export const setVOILUTRangeForMPR = async (range, convoluteFlag) => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const allViewports = renderingEngine.getVolumeViewports();
  allViewports.map((viewport) => {
    viewport.setVOI(range);
    // if (convoluteFlag) {
    //   convolute(viewport);
    // }
    viewport.render();
  });
};

export const enableVOISync = async () => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const allViewports = renderingEngine.getVolumeViewports();

  const synchronizer = getVOISynchronizer(VOI_SYNC_ID);

  allViewports.map((viewport) => {
    if (viewport) {
      synchronizer.addSource({
        renderingEngineId: RENDERING_ENGINE_ID,
        viewportId: viewport.id,
      });
      synchronizer.addTarget({
        renderingEngineId: RENDERING_ENGINE_ID,
        viewportId: viewport.id,
      });
    }
  });
};

export const getMPRViewport = async (mprViewportIndex) => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const viewport = renderingEngine.getViewport(
    MPRViewportsId[mprViewportIndex]
  );
  return viewport;
};

// The MPR toolbar's Crosshairs button is a visibility toggle, not just a
// primary-tool selector. Deselecting Crosshairs only demotes it to Passive (see
// setToolOnActiveMPRViewport below) so its reference lines survive a switch to a
// measurement tool — which meant crosshairs, on by default when MPR opens, could
// never be switched off. This flag is the explicit "off" state: it makes the
// deselect path disable the tool (dropping its annotations) and keeps later tool
// switches from quietly promoting it back to Passive.
let mprCrosshairsHidden = false;

export const areMPRCrosshairsHidden = () => mprCrosshairsHidden;

export const setMPRCrosshairsHidden = (hidden) => {
  mprCrosshairsHidden = hidden;

  const toolGroup = getToolGroupManager(MPR_TOOL_GROUP_ID);
  const toolName = cornerstonetools.CrosshairsTool.toolName;
  if (!toolGroup?._toolInstances?.[toolName]) {
    return;
  }

  // setToolDisabled runs CrosshairsTool.onSetToolDisabled(), which unbinds the
  // tool's listeners and removes its reference-line annotations; both mode
  // setters re-render the tool group's viewports themselves. Re-showing goes to
  // Passive here — the caller also makes Crosshairs the active tool, which
  // rebinds Primary on the next setToolOnActiveMPRViewport pass.
  if (hidden) {
    toolGroup.setToolDisabled(toolName);
  } else {
    toolGroup.setToolPassive(toolName);
  }
};

export const setToolOnActiveMPRViewport = async (
  viewportIndex,
  toolName,
  oldToolName,
  activeViewportIndex,
  mouseButtonToolAssignments
) => {
  syncPrimaryToolTo3DViewport(toolName, oldToolName);

  // Picking Crosshairs — from the toolbar, the mobile More menu, or a keyboard
  // shortcut — always means the user wants them visible again.
  if (toolName === cornerstonetools.CrosshairsTool.toolName) {
    mprCrosshairsHidden = false;
  }

  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const activeViewport = renderingEngine.getViewport(
    MPRViewportsId[viewportIndex]
  );

  // if (!activeViewport.getCurrentImageId()) {
  //   return;
  // }

  // const toolGroup = getToolGroupManager(TOOL_GROUP_ID + "_" + viewportIndex);
  const toolGroup = getToolGroupManager(MPR_TOOL_GROUP_ID);

  if (oldToolName) {
    // CrosshairsTool.onSetToolDisabled() tears down its listeners and removes
    // its reference-line annotations entirely — deselecting it in favor of a
    // measurement tool would wipe the crosshairs instead of just handing
    // Primary-button clicks over to the new tool. Passive keeps it rendered
    // and its reference lines draggable (same handle-drag path annotation
    // tools use below) so both stay usable together. The one exception is an
    // explicit "hide crosshairs" toggle (mprCrosshairsHidden), which has to fall
    // through to setToolDisabled so the lines actually come off the screen.
    if (
      PASSIVE_TOOLs.includes(oldToolName) ||
      (oldToolName === cornerstonetools.CrosshairsTool.toolName &&
        !mprCrosshairsHidden)
    ) {
      toolGroup.setToolPassive(oldToolName);
    } else if (!MOUSE_ASSIGNABLE_TOOL_NAMES.includes(oldToolName)) {
      toolGroup.setToolDisabled(oldToolName);
    }
  }

  if (!toolGroup._toolInstances?.[toolName]) {
    toolGroup.addTool(toolName);
  }

  // Flexpoly: mirror the stack-viewport path (setToolOnActiveViewport) so the
  // closed freehand ROI reports its area in MPR too.
  if (
    toolName === cornerstonetools.PlanarFreehandROITool.toolName &&
    viewportIndex === activeViewportIndex
  ) {
    toolGroup.setToolConfiguration(
      cornerstonetools.PlanarFreehandROITool.toolName,
      {
        calculateStats: true,
      },
      false
    );
  }

  const isReferenceLines =
    toolName === cornerstonetools.ReferenceLinesTool.toolName;

  if (isReferenceLines && viewportIndex === activeViewportIndex) {
    toolGroup.setToolConfiguration(
      cornerstonetools.ReferenceLinesTool.toolName,
      {
        sourceViewportId: activeViewport.id,
      },
      true
    );
    toolGroup.setToolEnabled(cornerstonetools.ReferenceLinesTool.toolName);
  }

  const assignments =
    mouseButtonToolAssignments &&
    typeof mouseButtonToolAssignments === "object"
      ? mouseButtonToolAssignments
      : DEFAULT_MOUSE_BUTTON_TOOL_ASSIGNMENTS;

  for (const assignableName of MOUSE_ASSIGNABLE_TOOL_NAMES) {
    deactivateAssignableCornerstoneTool(toolGroup, assignableName);
  }

  const activeIsAssignable = MOUSE_ASSIGNABLE_TOOL_NAMES.includes(toolName);
  const skipPrimaryForActiveTool = !activeIsAssignable && !isReferenceLines;

  const slotDefs = [
    ["primary", MouseBindings.Primary],
    ["secondary", MouseBindings.Secondary],
    ["auxiliary", MouseBindings.Auxiliary],
  ];

  for (const [slot, mouseBinding] of slotDefs) {
    if (slot === "primary" && skipPrimaryForActiveTool) {
      continue;
    }
    const assignedTool = assignments[slot];
    if (
      !assignedTool ||
      !MOUSE_ASSIGNABLE_TOOL_NAMES.includes(assignedTool)
    ) {
      continue;
    }
    try {
      toolGroup.setToolActive(assignedTool, {
        bindings: [{ mouseButton: mouseBinding }],
      });
    } catch (e) {
      console.warn("setToolActive failed for MPR", assignedTool, e);
    }
  }

  const inUse = new Set(
    [assignments.primary, assignments.secondary, assignments.auxiliary].filter(
      (n) => n && MOUSE_ASSIGNABLE_TOOL_NAMES.includes(n)
    )
  );
  for (const n of MOUSE_ASSIGNABLE_TOOL_NAMES) {
    if (!inUse.has(n)) {
      try {
        toolGroup.setToolDisabled(n);
      } catch {
      }
    }
  }

  if (!activeIsAssignable && !isReferenceLines) {
    try {
      toolGroup.setToolActive(toolName, {
        bindings: [{ mouseButton: MouseBindings.Primary }],
      });
    } catch (e) {
      console.warn("setToolActive failed for active MPR tool", toolName, e);
    }
  }
};

export const updateOrientationMarkers = async (event, viewportId) => {
  const renderingEngine = await getRenderingEngineLocal(RENDERING_ENGINE_ID);
  const viewport = renderingEngine.getViewport(viewportId);
  const { rotation, previousCamera, camera } = event.detail;
  let flipHorizontal = false;
  let flipVertical = false;
  if (
    camera &&
    camera.flipHorizontal !== undefined &&
    previousCamera.flipHorizontal !== camera.flipHorizontal
  ) {
    flipHorizontal = camera.flipHorizontal;
  }

  if (
    camera &&
    camera.flipVertical !== undefined &&
    previousCamera.flipVertical !== camera.flipVertical
  ) {
    flipVertical = camera.flipVertical;
  }

  let rowCosines, columnCosines;

  if (viewport.type === ViewportType.STACK) {
    const imageId = viewport.getCurrentImageId();
    if (!imageId) {
      return "";
    }
    // Defensive: a fresh wadors image hasn't necessarily finished loading
    // by the time CAMERA_MODIFIED fires (the engine warmup or a failed
    // /metadata fetch can leave the underlying image absent). Wrapping
    // loadAndCacheImage in try/catch keeps this listener from spewing
    // "Cannot read properties of undefined (reading 'samplesPerPixel')"
    // when Cornerstone's createImage trips on incomplete metadata —
    // we just skip the orientation update for this tick and let the
    // next CAMERA_MODIFIED retry once the image is settled.
    try {
      await loadAndCacheImage(imageId);
    } catch (err) {
      console.debug("[updateOrientationMarkers] image not ready yet:", err);
      return "";
    }
    const imagePlaneMetadta = cornerstone.metaData.get("imagePlaneModule", imageId);
    if (!imagePlaneMetadta) {
      return "";
    }
    ({ rowCosines, columnCosines } = imagePlaneMetadta);
  } else {
    const { viewUp, viewPlaneNormal } = viewport.getCamera();
    const viewRight = vec3.create();
    vec3.cross(viewRight, viewUp, viewPlaneNormal);

    columnCosines = [-viewUp[0], -viewUp[1], -viewUp[2]];
    rowCosines = viewRight;
  }

  if (!rowCosines || !columnCosines) {
    return "";
  }

  const rowString = orientation.getOrientationStringLPS(rowCosines);
  const columnString = orientation.getOrientationStringLPS(columnCosines);
  const oppositeRowString = orientation.invertOrientationStringLPS(rowString);
  const oppositeColumnString = orientation.invertOrientationStringLPS(columnString);

  let markers = {
    top: oppositeColumnString,
    left: oppositeRowString,
    right: rowString,
    bottom: columnString,
  };

  if (flipVertical) {
    markers.top = orientation.invertOrientationStringLPS(markers.top);
    markers.bottom = orientation.invertOrientationStringLPS(markers.bottom);
  }

  if (flipHorizontal) {
    markers.left = orientation.invertOrientationStringLPS(markers.left);
    markers.right = orientation.invertOrientationStringLPS(markers.right);
  }

  if (rotation === 90 || rotation === -270) {
    return {
      top: markers.left,
      left: orientation.invertOrientationStringLPS(markers.top),
      right: orientation.invertOrientationStringLPS(markers.bottom),
      bottom: markers.right, // left
    };
  } else if (rotation === -90 || rotation === 270) {
    return {
      top: orientation.invertOrientationStringLPS(markers.left),
      left: markers.top,
      bottom: markers.left,
      right: markers.bottom,
    };
  } else if (rotation === 180 || rotation === -180) {
    return {
      top: orientation.invertOrientationStringLPS(markers.top),
      left: orientation.invertOrientationStringLPS(markers.left),
      bottom: orientation.invertOrientationStringLPS(markers.bottom),
      right: orientation.invertOrientationStringLPS(markers.right),
    };
  }

  return markers;
};

export const loadAIImages = async (stack) => {
  stack.forEach(async (image, index) => {
    if (image !== "" && image !== null) await loadAndCacheImage(image);
  });
};
